import React, { ErrorInfo } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Link,
  Typography,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocationOn as LocationOnIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { GlobeAltIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import CustomInfoShieldIcon from '@/ui-library/CustomInfoShieldIcon';
import useNationalBallot from '@/hooks/useNationalBallot';
import useGuestAddress from '@/hooks/useGuestAddress';
import useGuestPersonalBallot from '@/hooks/useGuestPersonalBallot';
import {
  classifyNationalElectionType,
  STATE_NAMES,
  synthesizeBallotUserFromNational,
} from '@/utils/nationalBallotAdapter';
import {
  buildGuestPersonalContext,
  buildNationalSummaryContext,
  type GuestBallotUser,
  type AddressComponent,
} from '@/utils/guestChatContext';
import { useAuth } from '../auth/AuthProvider';
import { useMobileLayout } from '../../contexts/MobileLayoutContext';
import { BallotCandidate, Candidate } from './BallotCandidate';
import { candidateKey } from './candidateKey';
import BallotStatusButton from './BallotStatusButton';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { hasUpcomingElection } from '@/types/ballotRecords';
import type { MeasureBallotpediaBlock } from '@/types/ballotpedia';
import { YourPicksFilter, YourPicksFilterValue } from './YourPicksFilter';
import useLikedCandidates, { getLikedCandidateIds } from '@/hooks/useLikedCandidates';
import useMeasureSelections, {
  getMeasureSelections,
  MeasureVote,
} from '@/hooks/useMeasureSelections';
import useContestFilters from '@/hooks/useContestFilters';
import useExpandedContests from '@/hooks/useExpandedContests';
import useAnalytics from '@/hooks/useAnalytics';
import { primitiveColors, semanticColors, semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import { gradientBorderBackground } from '@/theme/utils';
import { BALLOTPEDIA_GAP_STATES } from '@/constants/ballotpediaConfig';
import { useProfileForm } from '@/hooks/useProfileForm';
import AutoCompleteAddressInput from '../signup/AutoCompleteAddressInput';
import { MinimalThinkingState } from '../chat/MinimalThinkingState';
import { useNotification } from '../ui-library/NotificationProvider';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import useApi from '@/hooks/useApi';
import { ContextService } from '@/services/contextService';
import { formatAddress } from '@/utils/addressFormat';
import {
  isSafeHttpUrl,
  officeLinkGroups,
  type ElectionOffice,
  type OfficeLink,
} from '@/utils/electionOfficeLinks';

import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useChat } from '@/hooks/useChat';
import { useDirection } from '@/hooks/useDirection';
import { getBallotActionState, type PendingBallotAction } from './ballotActionState';
import { isolate } from '@/utils/bidi';
import { useSurveyContext } from '@/contexts/SurveyContext';
import { EXTERNAL_URLS, openExternalUrl } from '@/constants/urls';
import { CHIP_COLOR } from '@/components/feedback-survey/FeedbackSurveyChip';

const PARTY_NAME_REPUBLICAN = 'Republican Party' as const;
const PARTY_NAME_DEMOCRATIC = 'Democratic Party' as const;

// aria-label keys for the per-measure Yes/No control, keyed by whether the
// ballot is editable and whether this choice is the recorded vote. Read-only
// records need their own selected/unselected pair so a saved Yes never reads
// as a vote on both choices.
const MEASURE_VOTE_ARIA_KEYS: Record<
  'cast' | 'remove' | 'readOnlySelected' | 'readOnlyUnselected',
  Record<MeasureVote, string>
> = {
  cast: {
    yes: 'elections.voteYesAriaLabel',
    no: 'elections.voteNoAriaLabel',
  },
  remove: {
    yes: 'elections.removeVoteYesAriaLabel',
    no: 'elections.removeVoteNoAriaLabel',
  },
  readOnlySelected: {
    yes: 'elections.voteReadOnlyYesAriaLabel',
    no: 'elections.voteReadOnlyNoAriaLabel',
  },
  readOnlyUnselected: {
    yes: 'elections.voteReadOnlyUnselectedYesAriaLabel',
    no: 'elections.voteReadOnlyUnselectedNoAriaLabel',
  },
};

// Alphabetical state groups for the national General Elections tab: 50 states + DC
// split into three even slices (pivoting around M/N) so 51 cards collapse behind
// three accordions. Only affects the national General tab; Primary & Special and
// personal-mode views are unaffected.
const ALPHA_GROUPS: ReadonlyArray<{
  id: string;
  label: string;
  states: ReadonlySet<string>;
}> = [
  {
    id: 'alpha-a-k',
    label: 'Alabama – Kentucky',
    states: new Set([
      'AL',
      'AK',
      'AZ',
      'AR',
      'CA',
      'CO',
      'CT',
      'DE',
      'DC',
      'FL',
      'GA',
      'HI',
      'ID',
      'IL',
      'IN',
      'IA',
      'KS',
      'KY',
    ]),
  },
  {
    id: 'alpha-l-n',
    label: 'Louisiana – North Dakota',
    states: new Set([
      'LA',
      'ME',
      'MD',
      'MA',
      'MI',
      'MN',
      'MS',
      'MO',
      'MT',
      'NE',
      'NV',
      'NH',
      'NJ',
      'NM',
      'NY',
      'NC',
      'ND',
    ]),
  },
  {
    id: 'alpha-o-w',
    label: 'Ohio – Wyoming',
    states: new Set([
      'OH',
      'OK',
      'OR',
      'PA',
      'RI',
      'SC',
      'SD',
      'TN',
      'TX',
      'UT',
      'VT',
      'VA',
      'WA',
      'WV',
      'WI',
      'WY',
    ]),
  },
];

// Month buckets for the national Primary & Special tab, parallel to ALPHA_GROUPS:
// three fixed aqua accordions tracking the primary calendar (early / peak / late).
// Months are 1-indexed (1 = January) to match the Date API after a +1 adjustment.
const MONTH_BUCKETS: ReadonlyArray<{
  id: string;
  label: string;
  months: ReadonlySet<number>;
}> = [
  { id: 'month-jan-apr', label: 'Jan – Apr', months: new Set([1, 2, 3, 4]) },
  { id: 'month-may-jul', label: 'May – Jul', months: new Set([5, 6, 7]) },
  { id: 'month-aug-dec', label: 'Aug – Dec', months: new Set([8, 9, 10, 11, 12]) },
];

interface Contest {
  id: string;
  name: string;
  title?: string;
  level: string;
  branch: string;
  districtName: string;
  districtType: string;
  contestType: string;
  ocdId: string;
  seatsUpForElection: number;
  partisanPrimary?: string;
  candidates?: Candidate[] | null;
}

interface BallotMeasure {
  id: string;
  name: string;
  shortName: string;
  streamlinedName: string;
  districtName: string;
  districtType: string;
  type: string;
  topics?: string[] | null;
  topicAreas: string[];
  summary?: string | null;
  yesVote: string;
  noVote: string;
  ballotQuestion?: string | null;
  ocdId: string;
  status: string;
  /** Ballotpedia enrichment — supporter/opponent coalitions, plain-language
      summary. Present when the backend matched this measure; see
      `@/types/ballotpedia`. Nothing renders it yet. */
  bp?: MeasureBallotpediaBlock;
}

interface VotingDates {
  election_day?: string;
  registration_online_date?: string;
  registration_mail_date?: string;
  registration_mail_postmarked_or_received?: string;
  registration_in_person_date?: string;
  registration_same_day?: boolean;
  early_voting_start?: string;
  early_voting_end?: string;
  early_voting_varies?: boolean;
  early_voting_url?: string;
  absentee_request_date?: string;
  absentee_return_mail_date?: string;
  absentee_postmarked_or_received?: string;
  absentee_return_in_person_date?: string;
  all_mail_send_start?: string;
  polls_close_time?: string;
}

interface Election {
  election_date: string;
  election_name: string;
  election_type: string;
  guidance_phase?: string;
  // Ballotpedia's candidate_lists_complete for this date (false = still filling
  // in). Absent when the geo pass didn't run for the address.
  bp_candidate_lists_complete?: boolean;
  is_past?: boolean;
  contests: Contest[];
  ballot_measures: BallotMeasure[];
  voting_dates?: VotingDates;
}

// Current elected official for the voter's address (Ballotpedia enrichment, on
// ballot_user.current_officeholders). Every field except name is optional.
// A single reach-out channel; type is "Email" | "Phone" | "Website".
interface OfficeholderContact {
  type: string;
  value: string;
}

interface Officeholder {
  name: string;
  office_name?: string;
  office_level?: string;
  office_branch?: string;
  office_type?: string;
  district_name?: string;
  party?: string;
  status?: string;
  date_assumed_office?: string;
  date_term_ends?: string;
  url?: string;
  contacts?: OfficeholderContact[];
}

interface BallotUser {
  id: string;
  next_election_date: string;
  polling_locations: Array<{
    election_date: string;
    election_name: string;
    polling_location_url: string;
  }>;
  elections: Election[];
  election_offices?: ElectionOffice[];
  current_officeholders?: Officeholder[];
  source: string;
  timestamp: string;
  updatedAt: string;
}

/**
 * Render mode. `personal` (default) loads the authed user's ballot, one tab per
 * election. `national` loads the public GET /ballot/national snapshot with two fixed
 * tabs (Primary & Special, General) — used for guests and users who cleared their address.
 */
export type SampleBallotMode = 'personal' | 'national';

interface SampleBallotViewProps {
  ballotData?: BallotUser;
  downloadRef?: React.MutableRefObject<(() => void) | null>;
  onIsDownloadingChange?: (v: boolean) => void;
  mode?: SampleBallotMode;
  /**
   * Mobile bottom-tab section this instance is showing. ``ballot`` renders the
   * election tabs only (Voting Info moves to its own bottom tab); ``info``
   * renders the Voting Info section with no tab strip. Omit on desktop, where
   * the panel keeps its own Voting Info + election tab strip.
   */
  mobileSection?: 'ballot' | 'info';
}

// Error Boundary for SampleBallotView
class SampleBallotErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('SampleBallotErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            backgroundColor: semanticColors.surfaceNeutral,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center',
              backgroundColor: semanticColors.surface,
              borderRadius: BORDER_RADIUS,
            }}
          >
            <Typography variant="h5" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
              {/* ErrorBoundary is a class component — no hook access, so read
                  translations off the shared i18n instance directly. */}
              {i18n.t('errorBoundary.title', { ns: 'ballot' })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {i18n.t('errorBoundary.description', { ns: 'ballot' })}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              sx={{ mr: 2, borderRadius: BORDER_RADIUS }}
            >
              {i18n.t('errorBoundary.reloadButton', { ns: 'ballot' })}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({ hasError: false });
              }}
              sx={{ borderRadius: BORDER_RADIUS }}
            >
              {i18n.t('errorBoundary.retryButton', { ns: 'ballot' })}
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

const decodeHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent ?? '';
};

// Ballotpedia measure yes_vote/no_vote are HTML (intro paragraph + optional
// <ul>). Parse to text + list items, dropping the "A yes/no vote" lead-in so it
// continues the YES/NO label; rendered as React elements (no raw HTML).
interface BpVoteContent {
  intro: string;
  items: string[];
}
const parseBpVote = (raw?: string): BpVoteContent | null => {
  if (!raw) return null;
  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const items = Array.from(doc.querySelectorAll('li'))
    .map((li) => (li.textContent ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const paragraph = doc.querySelector('p');
  const intro = (paragraph?.textContent ?? doc.body.textContent ?? '')
    .replace(/\s+/g, ' ')
    .replace(/^A\s+["“”]?(?:yes|no)["“”]?\s*vote\s*/i, '')
    .trim();
  if (!intro && items.length === 0) return null;
  return { intro, items };
};

/**
 * Suggested filename from a `Content-Disposition: attachment; filename="..."` header
 * (RFC 6266, including the `filename*=UTF-8''...` form), or null when the header is
 * absent — e.g. stripped by a proxy that doesn't expose it cross-origin.
 */
const filenameFromContentDisposition = (header: string | null): string | null => {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
};

const fmtDate = (d: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(d));

const fmtShort = (d: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(d));

// Map a Ballotpedia party string to a short ballot-style label (D, R, I…).
// Falls back to the full name with a trailing " Party" trimmed, so an
// unmapped affiliation still renders cleanly rather than disappearing.
const PARTY_ABBREVIATIONS: ReadonlyArray<{ test: RegExp; abbr: string }> = [
  { test: /^democrat/i, abbr: 'D' },
  { test: /^republican/i, abbr: 'R' },
  { test: /^libertarian/i, abbr: 'L' },
  { test: /^green/i, abbr: 'G' },
  { test: /^(?:independent|nonpartisan|no party)/i, abbr: 'I' },
];

const abbreviateParty = (party: string | undefined): string => {
  if (!party) return '';
  const trimmed = party.trim();
  const match = PARTY_ABBREVIATIONS.find((p) => p.test.test(trimmed));
  return match ? match.abbr : trimmed.replace(/\s+Party$/i, '');
};

// Group officeholders by level then branch so the voter scans broadest (Federal) to
// most local. Unrecognized values fall to the end under a shared "Other" label.
const LEVEL_ORDER = ['Federal', 'State', 'Local'] as const;
const BRANCH_ORDER = ['Executive', 'Legislative', 'Judicial'] as const;
const OTHER_GROUP = 'Other';

const orderedIndex = (order: ReadonlyArray<string>, value: string): number => {
  const idx = order.findIndex((o) => o.toLowerCase() === value.toLowerCase());
  return idx === -1 ? order.length : idx;
};

interface OfficeholderBranchGroup {
  branch: string;
  officeholders: Officeholder[];
}
interface OfficeholderLevelGroup {
  level: string;
  branches: OfficeholderBranchGroup[];
}

// Omit from the ballot: party chairs (private committee officers, not public
// officials) and judicial (a truncated slice of a long judge roster is misleading).
const isPartyChair = (oh: Officeholder): boolean =>
  (oh.office_type ?? '').trim().toLowerCase() === 'party chair';
const isJudicial = (oh: Officeholder): boolean =>
  (oh.office_branch ?? '').trim().toLowerCase() === 'judicial';

// Fixed contact-icon order so every officeholder shows the same sequence.
const CONTACT_ORDER: Record<string, number> = { website: 0, email: 1, phone: 2 };
const contactOrderIndex = (type: string): number =>
  CONTACT_ORDER[type.toLowerCase()] ?? Object.keys(CONTACT_ORDER).length;

const groupOfficeholders = (officeholders: Officeholder[]): OfficeholderLevelGroup[] => {
  const byLevel = new Map<string, Map<string, Officeholder[]>>();
  for (const oh of officeholders) {
    if (isPartyChair(oh) || isJudicial(oh)) continue;
    const level = oh.office_level?.trim() || OTHER_GROUP;
    const branch = oh.office_branch?.trim() || OTHER_GROUP;
    if (!byLevel.has(level)) byLevel.set(level, new Map());
    const branches = byLevel.get(level)!;
    if (!branches.has(branch)) branches.set(branch, []);
    branches.get(branch)!.push(oh);
  }
  return Array.from(byLevel.entries())
    .sort(([a], [b]) => orderedIndex(LEVEL_ORDER, a) - orderedIndex(LEVEL_ORDER, b))
    .map(([level, branches]) => ({
      level,
      branches: Array.from(branches.entries())
        .sort(([a], [b]) => orderedIndex(BRANCH_ORDER, a) - orderedIndex(BRANCH_ORDER, b))
        // Alphabetize officeholders by name within each branch.
        .map(([branch, ohs]) => ({
          branch,
          officeholders: [...ohs].sort((a, b) => a.name.localeCompare(b.name)),
        })),
    }));
};

// Map a contact channel to its href scheme (email→mailto, phone→sms, website→page).
// Returns null for anything unsafe or unrecognized so it renders no icon.
const officeholderContactHref = (contact: OfficeholderContact): string | null => {
  const value = contact.value?.trim();
  if (!value) return null;
  switch (contact.type.toLowerCase()) {
    case 'email':
      return `mailto:${value}`;
    case 'phone':
      return `sms:${value.replace(/[^\d+]/g, '')}`;
    case 'website':
      return isSafeHttpUrl(value) ? value : null;
    default:
      return null;
  }
};

const fmtTime = (t: string, locale: string) => {
  // Handle both ISO datetime strings and bare HH:MM
  const date = new Date(t);
  let h: number;
  let m: number;
  if (!Number.isNaN(date.getTime()) && t.includes('T')) {
    h = date.getHours();
    m = date.getMinutes();
  } else {
    const [hh, mm] = t.split(':');
    h = Number(hh);
    m = Number(mm ?? 0);
  }
  // Guard against malformed input: fall back to the raw string rather than
  // rendering a misleading time built from NaN values.
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    ...(m ? { minute: '2-digit' } : {}),
  }).format(new Date(2000, 0, 1, h, m));
};

const SampleBallotView: React.FC<SampleBallotViewProps> = React.memo(function SampleBallotView({
  downloadRef,
  onIsDownloadingChange,
  mode = 'personal',
  mobileSection,
}) {
  // Auth + guest-cache destructures hoisted here: the data-source selection and
  // currentBallotState memo below reference user/guestAddress, so declaring them
  // later would cause a TDZ ReferenceError when those computations run.
  const { t, i18n } = useTranslation('ballot');
  const locale = i18n.language;

  const { user, isAuthenticated, refreshUserProfile, getAccessTokenSilently } = useAuth();
  const {
    address: guestAddress,
    setAddress: setGuestAddress,
    clearAddress: clearGuestAddress,
  } = useGuestAddress();
  // BallotSheet context. useBallotSheet returns a noop default outside the provider
  // (desktop panel doesn't wrap with it), so calling it unconditionally is safe.
  const {
    detent: ballotSheetDetent,
    snapTo: snapBallotSheet,
    setPreviewTargetHeightPx,
    setLocality: setSheetLocality,
    // Additive: the coexistence tab model routes between bottom tabs via
    // setActiveTab. Only used when ``mobileSection`` is set; the sheet fields
    // above continue to drive the draggable-sheet layout when it is not.
    setActiveTab: setMobileTab,
  } = useBallotSheet();
  // useState-backed callback ref (not useRef) so the measurement effect re-runs when
  // the element mounts — the address Paper only renders after loading, not in the
  // initial skeleton, so a plain useRef would still be null at first effect run.
  const [sampleBallotViewRootEl, setSampleBallotViewRootEl] = React.useState<HTMLDivElement | null>(
    null
  );

  // Tapping the address input from the closed sheet raises it to give the input +
  // suggestions room. Snap target is browser-dependent: Chromium's keyboard overlays
  // content (viewport unchanged) so 'mid' is least disruptive; Safari shrinks the
  // viewport, so only 'full' (constant top) keeps the sheet from shifting. instant:
  // true suppresses the 280ms transition so Safari's scroll-into-view uses the final
  // input position, not a mid-animation one.
  const handleAddressInputInteract = React.useCallback(() => {
    if (ballotSheetDetent === 'closed') {
      const hasVirtualKeyboardApi =
        typeof navigator !== 'undefined' &&
        // @ts-expect-error VirtualKeyboard API not in default TS lib.
        Boolean(navigator.virtualKeyboard);
      snapBallotSheet(hasVirtualKeyboardApi ? 'mid' : 'full', { instant: true });
    }
  }, [ballotSheetDetent, snapBallotSheet]);

  // Measure the preview-visible height (HEADER 52 + addressArea.bottom − root.top) so
  // the BallotSheet rests exactly at the address row's bottom (= top of the tabs). The
  // safe-area inset is intentionally excluded — the sheet already shifts its CSS bottom
  // by env(safe-area-inset-bottom), so adding it here double-counts and exposes the tabs.
  // ResizeObserver re-measures whenever the address row mounts, resizes, or moves.
  React.useLayoutEffect(() => {
    const root = sampleBallotViewRootEl;
    if (!root) {
      setPreviewTargetHeightPx(null);
      return undefined;
    }
    const SHEET_HEADER_PX = 52;
    let measured = false;
    const tryMeasure = () => {
      if (measured) return;
      const addressEl = addressAreaRef.current;
      if (!addressEl) return;
      const rootRect = root.getBoundingClientRect();
      const addrRect = addressEl.getBoundingClientRect();
      // Only report when both elements have non-zero size.
      if (rootRect.height <= 0 || addrRect.height <= 0) return;
      const offsetWithinSampleBallotView = addrRect.bottom - rootRect.top;
      // `- 1` aligns the sheet's visible bottom with the address row's border;
      // without it subpixel rounding leaves a ~1px sliver of the tab strip showing.
      setPreviewTargetHeightPx(SHEET_HEADER_PX + offsetWithinSampleBallotView - 1);
      measured = true;
      // Tear down observers once measured: the offset is stable after layout, and
      // leaving them attached would re-fire on every drag and lag swipes.
      ro.disconnect();
      mo.disconnect();
    };
    // ResizeObserver catches the root/address el gaining size; MutationObserver
    // catches the skeleton → loaded DOM swap that adds the address el.
    const ro = new ResizeObserver(tryMeasure);
    ro.observe(root);
    const mo = new MutationObserver(tryMeasure);
    mo.observe(root, { childList: true, subtree: true });
    // Synchronous first attempt — succeeds when both refs are
    // already attached and sized at effect run.
    tryMeasure();
    return () => {
      ro.disconnect();
      mo.disconnect();
      setPreviewTargetHeightPx(null);
    };
  }, [sampleBallotViewRootEl, setPreviewTargetHeightPx]);

  // Data sources: (1) signed-in personal → the active per-election record from
  // BallotRecordsContext (activeRecord); (2) personal-guest (national mode +
  // cached address → /ballot/by-address); (3) national blank snapshot. Only (3)
  // uses the national tabbed layout; (1) and (2) share the personal layout.
  const national = useNationalBallot();
  const guestPersonal = useGuestPersonalBallot();
  const hasGuestAddress = mode === 'national' && Boolean(guestAddress?.formatted_address);

  // User-initiated "show me the national view" toggle (clear address + "See all
  // upcoming contests"). Pure UI state — leaves the saved address untouched so the
  // back link can restore the personal view cheaply.
  const [forceNationalView, setForceNationalView] = React.useState(false);
  const isNational = (mode === 'national' && !hasGuestAddress) || forceNationalView;

  const ballotRecords = useBallotRecords();

  // Effective US state for the personal ballot (drives surfaces like the Ballotpedia
  // gap-state notice): the DISPLAYED ballot's own state when signed in (its record
  // can be for a different state than the residence), parsed from the cached
  // address for guests, null in national view (state-specific surfaces don't apply).
  const currentBallotState = React.useMemo<string | null>(() => {
    if (isNational) return null;
    const recordLabel =
      ballotRecords.viewedRecord?.location_label || ballotRecords.activeRecord?.location_label;
    const recordState = recordLabel ? recordLabel.split(',').pop()?.trim() : undefined;
    if (recordState) return recordState;
    if (user?.state) return user.state;
    if (guestAddress?.address_components && Array.isArray(guestAddress.address_components)) {
      // Places API (New) emits shortText/longText; mirror parseAddressComponents'
      // shortName → short_name fallback so a shape drift doesn't drop the state abbrev.
      const stateComp = (
        guestAddress.address_components as Array<{
          types?: string[];
          shortText?: string;
          shortName?: string;
          short_name?: string;
        }>
      ).find((c) => Array.isArray(c.types) && c.types.includes('administrative_area_level_1'));
      return stateComp?.shortText || stateComp?.shortName || stateComp?.short_name || null;
    }
    return null;
  }, [
    isNational,
    ballotRecords.viewedRecord?.location_label,
    ballotRecords.activeRecord?.location_label,
    user?.state,
    guestAddress?.address_components,
  ]);

  // Whichever card is first in the Voting Info tab (the local-races notice when shown,
  // else the Election Info Sources accordion) squares its top-left corner to meet the
  // active tab's left edge, avoiding a rounded-corner sliver.
  const localRacesNoticeShown = Boolean(
    currentBallotState && BALLOTPEDIA_GAP_STATES.has(currentBallotState)
  );

  // Header locality string under "Your VoteMate Ballot Preview": national view shows an
  // address prompt; DC is hardcoded "Washington, DC" (Google Places gives it no county);
  // personal view shows "County, ST"; missing data → null (block doesn't render). County
  // for guests is parsed from address_components via the new-Places longText/shortText chain.
  const localityLabel = React.useMemo<string | null>(() => {
    if (isNational) return t('header.nationalLocality');
    // Name the DISPLAYED ballot's location (active or viewed record), not the residence.
    const recordLabel =
      ballotRecords.viewedRecord?.location_label || ballotRecords.activeRecord?.location_label;
    if (recordLabel) {
      return recordLabel;
    }
    if (currentBallotState === 'DC') return 'Washington, DC';
    if (user?.county && user?.state) return `${user.county}, ${user.state}`;
    if (guestAddress?.address_components && Array.isArray(guestAddress.address_components)) {
      const components = guestAddress.address_components as Array<{
        types?: string[];
        shortText?: string;
        longText?: string;
        shortName?: string;
        longName?: string;
        short_name?: string;
        long_name?: string;
      }>;
      const findByType = (type: string) =>
        components.find((c) => Array.isArray(c.types) && c.types.includes(type));
      const countyComp = findByType('administrative_area_level_2');
      const stateComp = findByType('administrative_area_level_1');
      const county =
        countyComp?.longText ||
        countyComp?.longName ||
        countyComp?.long_name ||
        countyComp?.shortText ||
        countyComp?.shortName ||
        countyComp?.short_name;
      const stateAbbr = stateComp?.shortText || stateComp?.shortName || stateComp?.short_name;
      if (county && stateAbbr) return `${county}, ${stateAbbr}`;
      return null;
    }
    return null;
  }, [
    isNational,
    ballotRecords.viewedRecord?.location_label,
    ballotRecords.activeRecord?.location_label,
    currentBallotState,
    user?.county,
    user?.state,
    guestAddress?.address_components,
    t,
  ]);

  // Mirror the locality into the BallotSheet context so the collapsed sheet
  // header can show it. No-op on desktop (noop setter).
  React.useEffect(() => {
    setSheetLocality(localityLabel);
  }, [localityLabel, setSheetLocality]);

  // Memoize the synthesized BallotUser — otherwise every state update re-runs the adapter
  // over all 51 states × elections × contests × candidates, the dominant national-mode cost.
  const nationalSynthesized = React.useMemo(
    () => (national.states ? synthesizeBallotUserFromNational(national.states) : null),
    // `locale` drives i18n.t inside synthesizeBallotUserFromNational (invisible to the
    // linter); without it, language changes leave card titles stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [national.states, locale]
  );
  // Hearts on a viewed record write to THAT record (not the live liked-candidates
  // store), and become read-only once every election on the record has passed.
  const viewedRecord = !isNational ? ballotRecords.viewedRecord : null;
  const updateViewedSelections = ballotRecords.updateViewedSelections;
  const refreshBallotRecords = ballotRecords.refreshRecords;
  const clearViewedRecord = ballotRecords.clearViewedRecord;
  const viewedHearts = React.useMemo(() => {
    if (!viewedRecord) return null;
    const ids = new Set(viewedRecord.selections?.candidates ?? []);
    return {
      isLiked: (id: string) => ids.has(id),
      toggle: (id: string) => {
        const next = new Set(ids);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        void updateViewedSelections(Array.from(next));
      },
    };
  }, [viewedRecord, updateViewedSelections]);
  const viewedHeartsReadOnly = viewedRecord ? !hasUpcomingElection(viewedRecord.elections) : false;

  // Signed-in personal path renders the ACTIVE ballot record (one election per
  // record) with tabs drawn from its sibling elections. Opening a history ballot
  // (viewedRecord) takes precedence; otherwise the live active record.
  const recordForView = !isNational ? (viewedRecord ?? ballotRecords.activeRecord) : null;
  const siblingTabs = recordForView?.sibling_ballots ?? null;

  // A per-election record holds only its own election's contests, so to keep the
  // multi-tab UI we synthesize a ballot whose elections span the sibling tabs:
  // only the active tab carries real contests; selecting another activates its
  // record, which swaps that election's data in.
  const recordViewBallot = React.useMemo(() => {
    if (isNational || !recordForView) return null;
    const activeElection = recordForView.elections?.[0];
    if (!siblingTabs || siblingTabs.length <= 1 || !activeElection) return recordForView;
    const elections = siblingTabs.map((sib) =>
      sib.id === recordForView.id
        ? activeElection
        : {
            election_date: sib.election_date,
            election_name: sib.election_name,
            election_type: sib.election_type,
            guidance_phase: sib.guidance_phase ?? undefined,
            is_past: sib.is_past,
            contests: [],
            ballot_measures: [],
          }
    );
    return { ...recordForView, elections };
  }, [isNational, recordForView, siblingTabs]);

  const initialBallot = isNational
    ? nationalSynthesized
    : recordViewBallot
      ? (recordViewBallot as unknown as BallotUser)
      : hasGuestAddress
        ? (guestPersonal.ballot as unknown as BallotUser)
        : null;
  const isInitialBallotLoading = isNational
    ? national.isLoading
    : ballotRecords.viewedLoading
      ? true
      : recordForView
        ? false
        : hasGuestAddress
          ? guestPersonal.isLoading
          : // Signed-in personal with no active record yet (deleting to landing,
            // or a fresh address building its ballot): show the skeleton while it
            // resolves. A genuine no-ballot state is the landing (DashboardLayout);
            // only a real fetch failure (activeRecordError) falls through to error.
            !ballotRecords.activeRecordError;
  const initialBallotError = isNational
    ? national.error
    : recordForView
      ? null
      : hasGuestAddress
        ? guestPersonal.error
        : ballotRecords.activeRecordError
          ? t('error.defaultMessage')
          : null;

  // Chat-context orchestration. The agent reads ConversationContext from sessionStorage
  // via ContextService. cosmos mode (signed-in personal) is populated by backend
  // /api/context/init; guest-personal, national-slim, and none are built client-side and
  // published via setContext. We re-call init only on transitions into 'cosmos' (avoids
  // hammering the API); setContext is in-memory, safe to call on every data change.
  // Signed-in user who deleted their last saved ballot: force context to 'none' so chat
  // ignores the still-stored profile address until they rebuild. Mirrors DashboardLayout.
  // Excludes the build window (first address / signup): records are transiently empty
  // there, and treating that as "no ballot" would clear context mid-build, flashing the
  // dynamic chips from context → none → context.
  const noSavedBallots =
    isAuthenticated &&
    mode === 'personal' &&
    !ballotRecords.recordsLoading &&
    !ballotRecords.building &&
    ballotRecords.records.length === 0;
  type ChatCtxMode = 'cosmos' | 'guest-personal' | 'national-slim' | 'none';
  const chatCtxMode: ChatCtxMode = (() => {
    if (noSavedBallots) return 'none';
    if (isAuthenticated && !forceNationalView && mode === 'personal') return 'cosmos';
    if (isNational) return national.states ? 'national-slim' : 'none';
    if (hasGuestAddress && guestPersonal.ballot && guestAddress?.address_components) {
      return 'guest-personal';
    }
    return 'none';
  })();
  const prevChatCtxModeRef = React.useRef<ChatCtxMode>('none');
  // Tracks whether initContext actually completed this cosmos session. Gating on
  // "previous mode === cosmos" failed: that ref advances synchronously at effect entry, so
  // a cancelled in-flight init (e.g. Auth0 token refresh) would skip re-init and leave
  // context uninitialized. This ref flips only after init resolves, so the next run re-fires.
  const cosmosInitDoneRef = React.useRef(false);
  // Location identity (county+state) for the authed ballot. On change, the cosmos context
  // re-inits so the chat's location-based quick prompts refresh on every real move.
  const cosmosLocationKeyRef = React.useRef<string | null>(null);
  const cosmosLocationKey =
    chatCtxMode === 'cosmos' ? `${user?.state ?? ''}|${user?.county ?? ''}` : null;
  React.useEffect(() => {
    let cancelled = false;
    const previous = prevChatCtxModeRef.current;
    prevChatCtxModeRef.current = chatCtxMode;

    if (chatCtxMode === 'cosmos') {
      if (ballotRecords.building) {
        // Record not on the backend yet (first address / signup): initing now
        // would cache an empty ballot. Hold — the init fires when building flips.
        if (previous !== 'cosmos') {
          ContextService.clearContext();
        }
      } else {
        // Re-init on transition into cosmos, when a prior init never completed, or when
        // the location changed — otherwise the quick prompts keep the old location.
        const locationChanged =
          previous === 'cosmos' && cosmosLocationKeyRef.current !== cosmosLocationKey;
        if (previous !== 'cosmos' || !cosmosInitDoneRef.current || locationChanged) {
          // On a real mode change into cosmos, clear stale context before init.
          if (previous !== 'cosmos') {
            ContextService.clearContext();
          }
          cosmosLocationKeyRef.current = cosmosLocationKey;
          getAccessTokenSilently()
            .then((token) => {
              if (!cancelled) {
                return ContextService.initContext(token);
              }
              return null;
            })
            .then(() => {
              if (!cancelled) cosmosInitDoneRef.current = true;
            })
            .catch(() => {});
        }
      }
    } else {
      // Leaving cosmos mode resets the done-ref so a subsequent return
      // (e.g. "Back to my ballot") will re-init.
      cosmosInitDoneRef.current = false;

      if (chatCtxMode === 'guest-personal' && guestPersonal.ballot && guestAddress) {
        ContextService.setContext(
          buildGuestPersonalContext(
            guestPersonal.ballot as GuestBallotUser,
            guestAddress.address_components as AddressComponent[] | undefined,
            // Guest's active language, so the first answer comes back in it
            // (FR-19). `locale` is in the dep list below, so a language switch
            // rebuilds the context instead of leaving a stale one.
            locale
          )
        );
      } else if (chatCtxMode === 'national-slim' && national.states) {
        ContextService.setContext(buildNationalSummaryContext(national.states, locale));
      } else if (chatCtxMode === 'none' && previous !== 'none') {
        ContextService.clearContext();
      }
    }

    return () => {
      cancelled = true;
    };
  }, [
    chatCtxMode,
    cosmosLocationKey,
    ballotRecords.building,
    national.states,
    guestPersonal.ballot,
    guestAddress,
    getAccessTokenSilently,
    locale,
  ]);

  // National-mode tab definitions, derived from the snapshot so we don't render an empty
  // "Primary & Special" or "General" tab. The content panel filters the flat elections
  // array by active category.
  type NationalTabCategory = 'primary_special' | 'general';
  const matchesNationalCategory = (election: Election, category: NationalTabCategory): boolean => {
    const type = (election as Election & { election_type?: string }).election_type;
    if (category === 'general') return type === 'general';
    return type !== 'general';
  };
  const nationalTabs = React.useMemo<
    Array<{ category: NationalTabCategory; label: string }>
  >(() => {
    // Inline classification (mirrors matchesNationalCategory) so the memo doesn't depend
    // on a non-stable function reference. A tab appears only if its category has upcoming
    // elections, ordered by nearest-upcoming date. ISO-8601 strings sort chronologically
    // as strings, so a plain comparison suffices.
    const flat: Election[] =
      isNational && initialBallot?.elections ? (initialBallot.elections as Election[]) : [];
    let earliestPrimarySpecial: string | null = null;
    let earliestGeneral: string | null = null;
    // Track which non-general types are present for the dynamic primary_special label.
    // 'other' (unmatched DW descriptions) is grouped with 'special' — both are one-off events.
    let hasPrimary = false;
    let hasNonPrimary = false;
    for (const e of flat) {
      const type = (e as Election & { election_type?: string }).election_type;
      const date = e.election_date;
      if (!date) continue;
      if (type === 'general') {
        if (earliestGeneral == null || date < earliestGeneral) earliestGeneral = date;
      } else {
        if (earliestPrimarySpecial == null || date < earliestPrimarySpecial) {
          earliestPrimarySpecial = date;
        }
        if (type === 'primary') hasPrimary = true;
        else hasNonPrimary = true;
      }
    }
    const primarySpecialLabel =
      hasPrimary && hasNonPrimary
        ? t('tabs.primarySpecial')
        : hasPrimary
          ? t('tabs.primaryOnly')
          : t('tabs.specialOnly');
    const tabsWithDate: Array<{
      category: NationalTabCategory;
      label: string;
      earliest: string;
    }> = [];
    if (earliestPrimarySpecial != null) {
      tabsWithDate.push({
        category: 'primary_special',
        label: primarySpecialLabel,
        earliest: earliestPrimarySpecial,
      });
    }
    if (earliestGeneral != null) {
      tabsWithDate.push({
        category: 'general',
        label: t('tabs.general'),
        earliest: earliestGeneral,
      });
    }
    tabsWithDate.sort((a, b) => a.earliest.localeCompare(b.earliest));
    return tabsWithDate.map(({ category, label }) => ({ category, label }));
  }, [isNational, initialBallot, t]);

  // Lifted from the JSX IIFE so the analytics effects can read the tab list without
  // re-deriving it. Stable identity across renders keeps the effects from re-firing.
  const tabEntries = React.useMemo<Array<{ key: string; label: string }>>(() => {
    if (!initialBallot?.elections || initialBallot.elections.length === 0) return [];
    if (isNational) return nationalTabs.map((t) => ({ key: t.category, label: t.label }));
    // Name each tab after the DISPLAYED ballot's own state (from its location
    // label, e.g. "Fauquier County, VA" → "VA"), not the residence address —
    // the active record can be for a different address than where the user lives.
    const recordLabel = recordForView?.location_label;
    const recordStateAbbrev = recordLabel ? recordLabel.split(',').pop()?.trim() : undefined;
    return initialBallot.elections.map((election: Election, i: number) => {
      const year = election.election_date ? new Date(election.election_date).getUTCFullYear() : '';
      const stateAbbrev = recordStateAbbrev || currentBallotState || user?.state || '';
      const electionName = election.election_name || t('elections.fallbackName');
      const type = classifyNationalElectionType(electionName);
      const electionType =
        type === 'general'
          ? t('tabs.generalElection')
          : type === 'primary'
            ? t('tabs.primaryElection')
            : type === 'special'
              ? t('tabs.specialElection')
              : t('tabs.election');
      return {
        key: election.election_date ?? `election-${i}`,
        label: `${year} ${stateAbbrev} ${electionType}`,
      };
    });
  }, [
    isNational,
    initialBallot,
    nationalTabs,
    currentBallotState,
    recordForView?.location_label,
    user?.state,
    t,
  ]);

  // Three-bucket mode label for analytics (vs. the finer chatCtxMode): 'national'
  // snapshot, 'personal' authenticated, or 'guest-personal' (guest with saved address).
  const trackedBallotMode: 'national' | 'personal' | 'guest-personal' = isNational
    ? 'national'
    : isAuthenticated
      ? 'personal'
      : 'guest-personal';

  const query = useApi();
  const {
    address: ballotAddress,
    handleAddressChange: handleBallotAddressChange,
    handleAddressSelect: handleBallotAddressSelectBase,
  } = useProfileForm();

  // Mobile detection and layout context
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobileLayout = useMobileLayout();
  const dir = useDirection();
  const { sendChat, isProcessing } = useChat();
  const { trackBallotItemViewed, openSurveyPanel } = useSurveyContext();
  const [pendingAction, setPendingAction] = React.useState<PendingBallotAction | null>(null);

  React.useEffect(() => {
    if (!isProcessing) {
      setPendingAction(null);
    }
  }, [isProcessing]);

  const [savedAddress, setSavedAddress] = React.useState('');
  const [pendingAddressDetails, setPendingAddressDetails] = React.useState<
    Parameters<typeof handleBallotAddressSelectBase>[0] | null
  >(null);
  const addressAreaRef = React.useRef<HTMLDivElement>(null);
  const [addressTransitioning, setAddressTransitioning] = React.useState(false);

  // Post-sign-in guest-cache cleanup: once a signed-in user has completed setup,
  // their Cosmos profile is the source of truth, so drop any lingering guest
  // address cache. While setup is still incomplete the signup modal owns address
  // capture (it pre-fills from this same cache and its submit writes the profile
  // + ballot record). Leaving the cache in place until the user submits keeps the
  // guest ballot on screen — clearing it early would strand the view on the
  // national snapshot mid-signup.
  React.useEffect(() => {
    if (!isAuthenticated || !user || !guestAddress?.formatted_address) return;
    if (user.initialSetupComplete) clearGuestAddress();
  }, [isAuthenticated, user, guestAddress?.formatted_address, clearGuestAddress]);

  // Load current address from the settings API (signed-in) or guest cache
  // (unauthenticated). Signed-in takes priority so a Cosmos address wins over a stale cache.
  React.useEffect(() => {
    if (user) {
      query('/user/settings')
        .then((r) => r.json())
        .then((data) => {
          const addr = formatAddress(data?.data?.address_components);
          setSavedAddress(addr);
          handleBallotAddressChange(addr);
        })
        .catch(() => {});
    } else if (guestAddress?.formatted_address) {
      setSavedAddress(guestAddress.formatted_address);
      handleBallotAddressChange(guestAddress.formatted_address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, guestAddress?.formatted_address]);

  // Reflect the on-screen ballot's address in the bar (record's address, else the
  // saved/guest address, else empty in forced-national). Keyed on the record id AND
  // savedAddress: the record id covers ballot switches, and savedAddress re-syncs
  // once the guest/saved address loads on mount (guests have no record to key off).
  // Neither changes on keystrokes, so in-progress edits aren't clobbered. Clears any
  // pending new-address entry.
  const activeAddressKey = recordForView?.id ?? null;
  React.useEffect(() => {
    setPendingAddressDetails(null);
    handleBallotAddressChange(
      recordForView
        ? recordForView.address_full || recordForView.location_label || ''
        : forceNationalView
          ? ''
          : savedAddress
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAddressKey, savedAddress]);

  // forceNationalView is a dep so this re-binds when the user enters See-all-upcoming:
  // there they browse without an address, so a revert leaves the field empty rather than
  // snapping back to the saved address.
  const revertAddress = React.useCallback(() => {
    setAddressTransitioning(true);
    setTimeout(() => {
      // Restore the address of whatever ballot is on screen: the viewed
      // record's address when browsing history, else the live/current one.
      handleBallotAddressChange(
        recordForView
          ? recordForView.address_full || recordForView.location_label || ''
          : forceNationalView
            ? ''
            : savedAddress
      );
      setPendingAddressDetails(null);
      setAddressTransitioning(false);
    }, 150);
  }, [savedAddress, forceNationalView, handleBallotAddressChange, recordForView]);

  // Revert address when user clicks outside the address area
  React.useEffect(() => {
    if (!pendingAddressDetails) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (addressAreaRef.current?.contains(e.target as Node)) return;
      revertAddress();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [pendingAddressDetails, revertAddress]);

  const handleBallotAddressSelectPending = React.useCallback(
    (addressDetails: Parameters<typeof handleBallotAddressSelectBase>[0]) => {
      handleBallotAddressSelectBase(addressDetails);
      setPendingAddressDetails(addressDetails);
    },
    [handleBallotAddressSelectBase]
  );

  // Locale-aware month names. Index 0 is blank so index 1 = January, etc.,
  // mirroring the MONTH_NAMES_LONG module-level constant which this replaces.
  const monthNamesLong = React.useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
    return [
      '',
      ...Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2024, i, 1)))),
    ];
  }, [locale]);

  // Pulled up so ``handleSaveBallotAddress`` and the JSX-level event
  // emitters share a single ``analytics`` reference.
  const analytics = useAnalytics();
  const { showNotification } = useNotification();

  const handleSaveBallotAddress = React.useCallback(async () => {
    if (!pendingAddressDetails) return;
    // Capture intent up-front so analytics reflects the pre-save state: hadSavedAddress
    // distinguishes a first-time entry from an update; wasNationalView separates a
    // "Back to my ballot" replacement from a plain overwrite.
    const hadSavedAddress = savedAddress.trim().length > 0;
    const wasNationalView = forceNationalView;
    // Guest path: stash the address in localStorage so the personalized ballot renders
    // without sign-in. useGuestPersonalBallot reacts and fetches /ballot/by-address.
    if (!isAuthenticated) {
      const newAddress = pendingAddressDetails.formatted_address;
      setGuestAddress({
        formatted_address: newAddress,
        address_components: pendingAddressDetails.address_components,
      });
      handleBallotAddressChange(newAddress);
      setSavedAddress(newAddress);
      setPendingAddressDetails(null);
      setForceNationalView(false);
      if (!hadSavedAddress) {
        analytics.track('Guest Chat Address Added', {
          source: 'Ballot Address Bar',
        });
      } else {
        analytics.track('Sample Ballot Address Changed', {
          from: wasNationalView ? 'national_view' : 'existing_address',
        });
      }
      return;
    }
    setIsRefetching(true);
    refetchStartActiveIdRef.current = ballotRecords.activeBallotId;
    try {
      await query('/user/settings', {
        method: 'POST',
        body: JSON.stringify({
          email: user?.email,
          address_payload: {
            formatted_address: pendingAddressDetails.formatted_address,
            address_components: pendingAddressDetails.address_components,
          },
        }),
      });
      refreshUserProfile(); // fire-and-forget — no need to await before refetching ballot
      const newAddress = pendingAddressDetails.formatted_address;
      handleBallotAddressChange(newAddress);
      setSavedAddress(newAddress);
      setPendingAddressDetails(null);
      setForceNationalView(false);
      // Saving a new address creates a new live ballot record; drop any viewed saved
      // record so the pane shows the freshly built ballot, not the one that was open.
      clearViewedRecord();
      // Authed users always have an address (set during signup), so this
      // is always an update — never a first-time add.
      analytics.track('Sample Ballot Address Changed', {
        from: wasNationalView ? 'national_view' : 'existing_address',
      });
      // The address change created a new ballot record server-side — refresh the
      // record list; the active record (and thus the ballot view) follows from it.
      void refreshBallotRecords();
      // Re-initialize conversation context so chat (quick prompts, agent system
      // prompt) reflects the new address's ballot. Fire-and-forget; the service
      // notifies subscribers when it resolves.
      getAccessTokenSilently()
        .then((accessToken) => ContextService.initContext(accessToken))
        .catch(() => {});
    } catch {
      setIsRefetching(false);
      // silent — user can update address in Settings
    }
  }, [
    pendingAddressDetails,
    isAuthenticated,
    setGuestAddress,
    query,
    user?.email,
    refreshUserProfile,
    refreshBallotRecords,
    clearViewedRecord,
    handleBallotAddressChange,
    getAccessTokenSilently,
    savedAddress,
    forceNationalView,
    analytics,
    ballotRecords.activeBallotId,
  ]);

  // Per-election partisan toggle: each (state, election) tracks its own Dem/Rep selection
  // independently. Defaults to Republican (the prior global default) until one is recorded.
  const [partyByElection, setPartyByElection] = React.useState<Record<string, string>>({});
  const [expandedMeasures, setExpandedMeasures] = React.useState<Record<string, boolean>>({});
  const [expandedContests, setExpandedContests] = useExpandedContests();
  const [contestFilters, setContestFilters] = useContestFilters();
  const { isLiked: storeIsLiked } = useLikedCandidates();
  // isLiked reflects the on-screen ballot: a viewed record reads its own selections, else
  // the live store. Shared by the hearts display and Your Picks filter so they agree.
  const isLiked = React.useCallback(
    (id: string): boolean =>
      viewedRecord && viewedHearts ? viewedHearts.isLiked(id) : storeIsLiked(id),
    [viewedRecord, viewedHearts, storeIsLiked]
  );
  const { getVote, toggleVote } = useMeasureSelections();
  // Same idea for measure votes: a viewed record's votes write to THAT
  // record instead of the live measure-selections store.
  const viewedMeasureVotes = React.useMemo(() => {
    if (!viewedRecord) return null;
    const recordVotes = (viewedRecord.selections?.measures ?? {}) as Record<string, string>;
    return {
      getVote: (id: string): MeasureVote | null => {
        const vote = recordVotes[id];
        return vote === 'yes' || vote === 'no' ? vote : null;
      },
      toggle: (id: string, choice: MeasureVote) => {
        const next = { ...recordVotes };
        if (next[id] === choice) delete next[id];
        else next[id] = choice;
        void updateViewedSelections(viewedRecord.selections?.candidates ?? [], next);
      },
    };
  }, [viewedRecord, updateViewedSelections]);
  const handleToggleMeasureVote = React.useCallback(
    (measureId: string, choice: MeasureVote) => {
      let nextVote: MeasureVote | null;
      if (viewedMeasureVotes) {
        nextVote = viewedMeasureVotes.getVote(measureId) === choice ? null : choice;
        viewedMeasureVotes.toggle(measureId, choice);
      } else {
        nextVote = toggleVote(measureId, choice);
      }
      analytics.track('Sample Ballot Measure Voted', {
        measureId,
        vote: nextVote,
        fromRecord: Boolean(viewedMeasureVotes),
      });
    },
    [analytics, viewedMeasureVotes, toggleVote]
  );
  const effectiveGetMeasureVote = viewedMeasureVotes ? viewedMeasureVotes.getVote : getVote;
  const [expandedElections, setExpandedElections] = React.useState<Record<string, boolean>>({});
  // National General Elections tab: alphabetical state groups (collapsed
  // by default; users opt into a slice of the country at a time).
  const [expandedAlphaGroups, setExpandedAlphaGroups] = React.useState<Record<string, boolean>>({});
  // National Primary & Special tab: time buckets, collapsed by default. Same pattern as
  // expandedAlphaGroups — bucket IDs are namespaced (month-*) to avoid collision.
  const [expandedTimeBuckets, setExpandedTimeBuckets] = React.useState<Record<string, boolean>>({});
  const [selectedElectionTab, setSelectedElectionTab] = React.useState(0);
  const [votingInfoTabActive, setVotingInfoTabActive] = React.useState(false);
  const [electionOfficeHighlight, setElectionOfficeHighlight] = React.useState(false);
  const [votingDatesExpanded, setVotingDatesExpanded] = React.useState<Record<number, boolean>>({});
  const [votingDatesAccordionOpen, setVotingDatesAccordionOpen] = React.useState<
    Record<number, boolean>
  >({});
  const [pendingElectionOfficeHighlight, setPendingElectionOfficeHighlight] = React.useState(0);
  const electionOfficesRef = React.useRef<HTMLParagraphElement>(null);
  const lastProcessedHighlight = React.useRef(0);
  // Election offices for the disclaimer's authoritative-source sentence.
  const officeGroups = React.useMemo(
    () => officeLinkGroups((initialBallot as BallotUser)?.election_offices),
    [initialBallot]
  );
  // Short anchor text keeps every office link inside one sentence; an unknown
  // label from the API falls through to whatever it sent.
  const shortOfficeLabel = React.useCallback(
    (link: OfficeLink) =>
      link.shortLabelKey
        ? t(`votingInfo.electionOffices.shortLabels.${link.shortLabelKey}`, {
            defaultValue: link.label,
          })
        : link.label,
    [t]
  );
  // On the record path the selected tab is always the active sibling; selecting
  // another tab activates its record (below), which swaps the active election in.
  const activeSiblingIndex =
    siblingTabs && recordForView
      ? Math.max(
          0,
          siblingTabs.findIndex((s) => s.id === recordForView.id)
        )
      : null;
  const safeElectionTab =
    activeSiblingIndex ??
    Math.min(selectedElectionTab, Math.max((initialBallot?.elections?.length ?? 1) - 1, 0));
  // On the record path, selecting a tab activates its record asynchronously; the
  // highlight/aria-selected follow this optimistic index immediately so screen
  // readers hear the new tab as selected, while the panel content stays on the
  // resolved election until activation lands.
  const [pendingTabIndex, setPendingTabIndex] = React.useState<number | null>(null);
  const highlightedElectionTab = pendingTabIndex ?? safeElectionTab;

  // -- Tab analytics ------------------------------------------------
  // Each tab-change call site sets nextTabSelectionViaRef before updating tab state; the
  // effect consumes it to stamp `via` on Sample Ballot Tab Selected. Defaults to 'click'.
  const nextTabSelectionViaRef = React.useRef<'click' | 'keyboard' | 'programmatic'>('click');
  // Skip the initial render so we don't emit a Tab Selected event for the
  // default tab — that's already covered by ``Sample Ballot Tabs Available``.
  const tabSelectInitializedRef = React.useRef(false);
  // Skip-once flag the data-reset effect sets when it auto-resets the tab on a refetch,
  // so an address switch from a non-default tab doesn't emit a spurious Tab Selected event.
  const skipNextTabEventRef = React.useRef(false);

  // Election-tab selection. On the record path each tab is a sibling election;
  // choosing one activates its record so the address + chat context follow it,
  // and the highlight follows activeSiblingIndex once it swaps in. Elsewhere
  // (national/guest) it's the classic index into a single multi-election ballot.
  const handleElectionTabSelect = React.useCallback(
    (i: number, via: 'click' | 'keyboard') => {
      nextTabSelectionViaRef.current = via;
      setVotingInfoTabActive(false);
      if (siblingTabs) {
        const sib = siblingTabs[i];
        if (sib && recordForView && sib.id !== recordForView.id) {
          setPendingTabIndex(i);
          void ballotRecords.activateBallot(sib.id).finally(() => setPendingTabIndex(null));
        }
      } else {
        setSelectedElectionTab(i);
      }
    },
    [siblingTabs, recordForView, ballotRecords]
  );

  // -- Sample Ballot Tabs Available ---------------------------------
  // Fires once per unique tab signature (mode + count + types) to give every Tab Selected
  // event a denominator. Re-fires when the tab shape changes, quiet across other renders.
  const lastTabsSignatureRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!initialBallot?.elections || initialBallot.elections.length === 0) return;
    const electionTypes = isNational
      ? Array.from(new Set(nationalTabs.map((t) => t.category)))
      : Array.from(
          new Set(
            (initialBallot.elections as Election[]).map((e) => {
              const t = classifyNationalElectionType(e.election_name || '');
              return t === 'general'
                ? 'general'
                : t === 'primary'
                  ? 'primary'
                  : t === 'special'
                    ? 'special'
                    : 'other';
            })
          )
        );
    const signature = `${trackedBallotMode}|${tabEntries.length}|${electionTypes.slice().sort().join(',')}`;
    if (lastTabsSignatureRef.current === signature) return;
    lastTabsSignatureRef.current = signature;
    analytics.track('Sample Ballot Tabs Available', {
      mode: trackedBallotMode,
      election_tab_count: tabEntries.length,
      tab_count: tabEntries.length + 1, // +1 for the Voting Info tab
      election_types: electionTypes,
    });
  }, [trackedBallotMode, tabEntries, initialBallot, isNational, nationalTabs, analytics]);

  // -- Sample Ballot Tab Selected -----------------------------------
  // Fires whenever the user moves to a different tab. Skips the initial render — the
  // default-on-mount tab is implicit in Tabs Available.
  React.useEffect(() => {
    if (!tabSelectInitializedRef.current) {
      tabSelectInitializedRef.current = true;
      return;
    }
    if (skipNextTabEventRef.current) {
      skipNextTabEventRef.current = false;
      return;
    }
    const via = nextTabSelectionViaRef.current;
    nextTabSelectionViaRef.current = 'click'; // reset to default
    const totalTabs = tabEntries.length + 1;
    if (votingInfoTabActive) {
      analytics.track('Sample Ballot Tab Selected', {
        tab_type: 'voting_info',
        tab_index: 0,
        total_tabs: totalTabs,
        via,
      });
      return;
    }
    if (isNational) {
      const category = nationalTabs[safeElectionTab]?.category;
      analytics.track('Sample Ballot Tab Selected', {
        tab_type: 'national_category',
        tab_index: safeElectionTab + 1,
        total_tabs: totalTabs,
        national_category: category,
        via,
      });
      return;
    }
    const election = initialBallot?.elections?.[safeElectionTab];
    const electionTypeKey = election
      ? classifyNationalElectionType(election.election_name || '')
      : null;
    const electionType =
      electionTypeKey === 'general'
        ? 'General'
        : electionTypeKey === 'primary'
          ? 'Primary'
          : electionTypeKey === 'special'
            ? 'Special'
            : 'Other';
    analytics.track('Sample Ballot Tab Selected', {
      tab_type: 'election',
      tab_index: safeElectionTab + 1,
      total_tabs: totalTabs,
      election_date: election?.election_date,
      election_type: electionType,
      via,
    });
    // ``initialBallot`` deliberately omitted from deps — we only want to
    // fire on tab-index/state changes, not on every ballot data refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [votingInfoTabActive, safeElectionTab, isNational, tabEntries.length, analytics]);
  // Per-election key for the "Ballot not finalized" popover. A single {anchor, key} tuple
  // keeps them atomically in sync; each Popover gates open on key === election.election_date
  // so only the clicked chip's popover renders. A shared anchor previously gave every chip
  // the same aria state and opened N stacked popovers at once.
  const [incompletePopover, setIncompletePopover] = React.useState<{
    anchor: HTMLElement;
    key: string;
  } | null>(null);

  // Document-level Escape listener for the "Ballot not finalized" popover. We keep focus
  // on the chip (so re-Enter toggles close), which disables MUI's focus-dependent Escape
  // handler — this listener restores Escape regardless of focus location.
  React.useEffect(() => {
    if (!incompletePopover) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIncompletePopover(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [incompletePopover]);

  const [isMeasuresInitialized, setIsMeasuresInitialized] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const prevIsLoadingRef = React.useRef(false);
  // The active ballot id when a new-address refetch started, so we can tell when
  // the freshly built ballot has loaded (record-driven view).
  const refetchStartActiveIdRef = React.useRef<string | null>(null);
  const disclaimerRef = React.useRef<HTMLDivElement>(null);

  // Legacy/guest path: clear the "building ballot" overlay when the ballot hook
  // finishes loading.
  React.useEffect(() => {
    if (prevIsLoadingRef.current && !isInitialBallotLoading && isRefetching) {
      setIsRefetching(false);
    }
    prevIsLoadingRef.current = isInitialBallotLoading;
  }, [isInitialBallotLoading, isRefetching]);

  // Record-driven path: the new address's ballot has loaded once the active
  // record swaps to a different ballot than when the refetch started. A timeout
  // is the backstop so the overlay can never hang (e.g. re-saving the same
  // address, where the active record id doesn't change).
  React.useEffect(() => {
    if (!isRefetching) return undefined;
    if (recordForView?.id && recordForView.id !== refetchStartActiveIdRef.current) {
      setIsRefetching(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsRefetching(false), 8000);
    return () => clearTimeout(timer);
  }, [isRefetching, recordForView?.id]);

  // Fire pending highlight after the voting info tab has rendered
  React.useEffect(() => {
    if (votingInfoTabActive && pendingElectionOfficeHighlight > lastProcessedHighlight.current) {
      lastProcessedHighlight.current = pendingElectionOfficeHighlight;
      setElectionOfficeHighlight(true);
      electionOfficesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const t = setTimeout(() => setElectionOfficeHighlight(false), 1200);
      return () => clearTimeout(t);
    }
  }, [votingInfoTabActive, pendingElectionOfficeHighlight]);

  // Ballot identity ignoring selections: changes on address switch or opening a different
  // record, but not on hearting a candidate. Keying the reset on initialBallot identity
  // instead made every heart on a viewed record jump back to the first election.
  const ballotResetKey = React.useMemo(() => {
    const dates = (initialBallot?.elections ?? [])
      .map((e: { election_date?: string; date?: string }) => e.election_date || e.date || '')
      .join('|');
    return `${isNational ? 'national' : (viewedRecord?.id ?? 'current')}:${dates}`;
  }, [isNational, viewedRecord, initialBallot]);

  // Reset election expanded state when ballot data changes (e.g. address switch)
  // so only the nearest election auto-expands.
  React.useEffect(() => {
    // Tell the Tab Selected effect to ignore this reset — the user
    // didn't pick a new tab, the data changed underneath them.
    skipNextTabEventRef.current = true;
    setExpandedElections({});
    setSelectedElectionTab(0);
    // On the phone the bottom tab decides which section shows; keep it. When
    // ``mobileSection`` is undefined this matches main's prior ``false``.
    setVotingInfoTabActive(mobileSection === 'info');
    setVotingDatesExpanded({});
    setVotingDatesAccordionOpen({});
    // ``mobileSection`` deliberately omitted — its own sync effect handles it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ballotResetKey]);

  const hasTrackedBallotViewRef = React.useRef(false);

  // Track ballot view once data loads (contests are auto-expanded) — fires only once per mount
  React.useEffect(() => {
    if (initialBallot?.elections?.length && !hasTrackedBallotViewRef.current) {
      hasTrackedBallotViewRef.current = true;
      trackBallotItemViewed();
    }
  }, [initialBallot, trackBallotItemViewed]);

  React.useEffect(() => {
    const el = disclaimerRef.current;
    if (!el || !initialBallot?.elections) return;

    const rafId = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      const initial: Record<string, boolean> = {};
      initialBallot.elections.forEach((election: Election) => {
        (election.ballot_measures ?? []).forEach((m: BallotMeasure) => {
          initial[m.id] = isVisible;
        });
      });
      setExpandedMeasures(initial);
      setIsMeasuresInitialized(true);
    });

    return () => cancelAnimationFrame(rafId);
  }, [initialBallot]);

  // Shared sx for Voting Info section accordion headers (Election Offices, Voting Dates, etc.)
  const votingInfoSectionHeaderSx = {
    backgroundColor: semanticColors.themeAqua,
    color: semanticColors.textPrimary,
    borderRadius: BORDER_RADIUS,
    '&.Mui-expanded': {
      borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
    },
    p: 1,
    minHeight: 'unset',
    position: 'relative',
    '& .MuiAccordionSummary-content': {
      my: 0,
      flex: 1,
      minWidth: 0,
      alignItems: 'center',
      gap: 1,
      pr: 3,
    },
    '& .MuiAccordionSummary-expandIconWrapper': (t: any) => ({
      position: 'absolute',
      right: t.spacing(1),
      top: '50%',
      transform: 'translateY(-50%)',
      '&.Mui-expanded': {
        transform: 'translateY(-50%) rotate(180deg)',
      },
    }),
  } as const;

  // On mobile a control that hands a prompt to the chat also returns to the
  // conversation. In the tab model (``mobileSection`` set) that means switching
  // to the Chat tab; in the draggable-sheet model it closes the sheet. Stable
  // identity so BallotCandidate's React.memo can skip re-rendering on sheet-drag
  // state changes; a fresh function every render would re-render every candidate.
  const handleCloseBallot = React.useCallback(() => {
    if (isMobile) {
      if (mobileSection) {
        setMobileTab('chat');
      } else {
        mobileLayout.setBallotOpen(false);
      }
    }
  }, [isMobile, mobileSection, setMobileTab, mobileLayout]);

  // Open the Voting Info (shield) tab and move focus to it. The triggering chip lives in
  // an election panel that unmounts on switch, so without the focus move keyboard focus
  // would drop to the body; focusing also sets aria-selected to match the visual state.
  // In the tab model the Voting Info section is its own bottom tab (My Info), so switch
  // there instead — the ``mobileSection`` sync effect below flips the panel.
  const openVotingInfoTab = React.useCallback(() => {
    if (mobileSection) {
      setMobileTab('info');
      return;
    }
    setVotingInfoTabActive(true);
    requestAnimationFrame(() => {
      document.getElementById('voting-info-tab')?.focus();
    });
  }, [mobileSection, setMobileTab]);

  // Bottom tab → panel sync. The bottom tab bar already reports the switch
  // (``Bottom Tab Selected``), so skip the in-panel Tab Selected event for it.
  React.useEffect(() => {
    if (!mobileSection) return;
    skipNextTabEventRef.current = true;
    setVotingInfoTabActive(mobileSection === 'info');
  }, [mobileSection]);

  // Detect if a specific election has partisan races
  const electionHasPartisanRaces = React.useCallback((election: Election) => {
    if (!election.contests) return false;
    const contestNames = new Map<string, Set<string>>();
    for (const contest of election.contests) {
      if (contest.partisanPrimary) {
        if (!contestNames.has(contest.name)) {
          contestNames.set(contest.name, new Set());
        }
        contestNames.get(contest.name)?.add(contest.partisanPrimary);
      }
    }
    for (const parties of contestNames.values()) {
      if (parties.size > 1) return true;
    }
    return false;
  }, []);

  // Filter contests by the party selected *for this election*. The party arg (vs. a
  // global closure) lets each election card filter with its own toggle state.
  const filterContestsByParty = React.useCallback(
    (election: Election, party: string) => {
      const contests = election.contests || [];
      if (party === 'all' || !electionHasPartisanRaces(election)) {
        return contests;
      }

      return contests.filter((contest) => {
        // Always show non-partisan contests
        if (!contest.partisanPrimary) {
          return true;
        }

        // Show contests matching the selected party
        return contest.partisanPrimary === party;
      });
    },
    [electionHasPartisanRaces]
  );

  const selectParty = React.useCallback((electionKey: string, party: string) => {
    setPartyByElection((prev) =>
      prev[electionKey] === party ? prev : { ...prev, [electionKey]: party }
    );
    // Preserve `expandedContests` here — collapse intent is persisted (useExpandedContests)
    // and should survive partisan switches, as it does across tabs / settings / close.
    setExpandedMeasures({});
  }, []);

  const formattedDate = React.useMemo(
    () => (initialBallot?.next_election_date ? new Date(initialBallot.next_election_date) : null),
    [initialBallot?.next_election_date]
  );

  // Detect a past election date. Democracy Works may keep a concluded election in an
  // "active" guidance phase, so this guards against showing stale dates. Compare at UTC
  // midnight to match the stored date format.
  const todayUTC = new Date();
  todayUTC.setUTCHours(0, 0, 0, 0);
  const isElectionDatePast = formattedDate !== null && formattedDate < todayUTC;

  // PDF download logic — fetches the server-rendered ballot PDF (POST /ballot/export.pdf)
  // rather than building it client-side.
  const handleDownloadBallot = React.useCallback(async () => {
    if (!initialBallot) return;

    onIsDownloadingChange?.(true);
    // iOS Safari (and every other iOS browser, which shares its WebKit engine) doesn't
    // reliably honor a programmatic anchor `download` click on a blob: URL, so the PDF
    // opens in a new tab there instead, from which the user can save or share it. The
    // tab has to be opened synchronously, inside the click's user-gesture window — a
    // `window.open` issued after the fetch resolves is exactly what the popup blocker
    // rejects. It starts blank and is pointed at the blob once the PDF arrives.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const pdfTab = isIOS ? window.open('', '_blank') : null;
    try {
      // Routed through useAnalytics like every other event in this file, so it
      // honors the synthetic-session opt-out (config/synthetic.ts). A direct
      // mixpanel.track here would also run against an uninitialized SDK in
      // those sessions, since the provider skips init entirely.
      analytics.track('Download Ballot PDF', {
        userId: user?.id,
        electionDate: initialBallot.next_election_date,
        timestamp: new Date().toISOString(),
      });

      const response = await query('/ballot/export.pdf', {
        method: 'POST',
        body: JSON.stringify({
          liked_candidate_ids: getLikedCandidateIds(),
          party_by_election: partyByElection,
          measure_selections: getMeasureSelections(),
        }),
      });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location.href = objectUrl;
      } else {
        const link = document.createElement('a');
        link.href = objectUrl;
        // The server names the file per state and election date
        // (`votemate_ballot_<STATE>_<DATE>.pdf`), so a voter who saves ballots for two
        // elections gets two distinct files rather than `votemate_ballot (1).pdf`.
        link.download =
          filenameFromContentDisposition(response.headers.get('content-disposition')) ??
          'votemate_ballot.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Deferred so the new-tab load (iOS) or download click (other browsers) has time
      // to read the blob before the URL is invalidated; the tab path gets longer since
      // it involves a full page navigation.
      setTimeout(() => URL.revokeObjectURL(objectUrl), pdfTab ? 60000 : 1000);
    } catch (err) {
      // A failed export shouldn't leave a blank tab behind.
      pdfTab?.close();
      console.error('Error generating ballot PDF:', err);
      showNotification(t('requestFailed', { ns: 'common' }), 'error');
    } finally {
      onIsDownloadingChange?.(false);
    }
  }, [
    initialBallot,
    onIsDownloadingChange,
    partyByElection,
    query,
    analytics,
    user,
    t,
    showNotification,
  ]);

  // Register download handler with parent via ref
  React.useEffect(() => {
    if (downloadRef) {
      downloadRef.current = handleDownloadBallot;
    }
    return () => {
      if (downloadRef) downloadRef.current = null;
    };
  }, [downloadRef, handleDownloadBallot]);

  // Show the "building" overlay until the ballot has elections (or error on failure); also
  // during a user-triggered refetch so there's one loading surface, not two. Returns early
  // so the main render path never runs with a null/incomplete initialBallot.
  const hasElections = initialBallot?.elections && initialBallot.elections.length > 0;
  if (!initialBallot || (!hasElections && isInitialBallotLoading) || isRefetching) {
    // Fetch finished but no data → error state
    if (!isInitialBallotLoading && !initialBallot) {
      return (
        <Box
          sx={{
            // 100% (not 100vh) so it fills its parent; 100vh would overflow by the
            // container chrome above (mobile header / desktop banner) and clip the bottom.
            height: '100%',
            backgroundColor: semanticColors.surfaceLight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center',
              backgroundColor: semanticColors.surface,
              borderRadius: BORDER_RADIUS,
            }}
          >
            <Typography variant="h5" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('error.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {initialBallotError ?? t('error.defaultMessage')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('error.hint')}
            </Typography>
          </Paper>
        </Box>
      );
    }

    // Still loading — show ballot shell with frosted overlay
    return (
      <Box
        aria-busy="true"
        sx={{
          height: '100%',
          // Matches the loaded-state outer Box.
          backgroundColor: { xs: primitiveColors.blue1, md: semanticColors.surfaceLight },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Header skeleton — mirrors the loaded header to prevent layout shift */}
        <Box
          sx={{
            p: 1,
            backgroundColor: { xs: semanticColors.ballotElectionCard, md: semanticColors.surface },
            position: 'relative',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'flex-start', lg: 'center' },
            justifyContent: {
              xs: 'flex-start',
              md: 'space-between',
            },
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              flexDirection: 'row',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: 0, md: 40 },
                height: { xs: 0, md: 36 },
                display: { xs: 'none', md: 'block' },
              }}
            >
              <VotemateLogo size={40} logoOnly />
            </Box>
            <Box>
              <Typography
                component="h1"
                fontWeight="bold"
                sx={{
                  fontSize: 16,
                  fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 1.3,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {t('loading.title')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Frosted overlay */}
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <MinimalThinkingState size={60} aria-hidden="true" />
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              color: 'text.secondary',
              fontWeight: 500,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {t('loading.building')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const headerTitleSize = 16;

  return (
    <Box
      ref={setSampleBallotViewRootEl}
      sx={{
        height: '100%',
        // Blue1 on mobile to match the chat scroll container's palette (same bottom-sheet);
        // surfaceLight on desktop where the sheet doesn't apply.
        backgroundColor: { xs: primitiveColors.blue1, md: semanticColors.surfaceLight },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header + Address Row share one background paint (separate backgrounds showed a
          subpixel hairline between them at some DPRs). Mobile uses the teal election-card
          color to flow into the grabber band; desktop keeps white to match the cards. */}
      <Box
        sx={{
          backgroundColor: { xs: semanticColors.ballotElectionCard, md: semanticColors.surface },
        }}
      >
        {/* Header — desktop only. On mobile the logo, title, and locality live in the
          sheet's teal band, so hiding this on xs removes empty padding above the address row. */}
        <Box
          sx={{
            p: 1,
            position: 'relative',
            display: { xs: 'none', md: 'flex' },
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'flex-start', lg: 'center' },
            justifyContent: {
              xs: 'flex-start',
              md: 'space-between',
            },
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              flexDirection: 'row',
            }}
          >
            {/* Hidden on mobile — the logo lives in the BallotSheet's teal band there;
              on desktop it anchors the white header. */}
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: 0, md: 40 },
                height: { xs: 0, md: 36 },
                display: { xs: 'none', md: 'block' },
              }}
            >
              <VotemateLogo size={40} logoOnly />
            </Box>
            <Box>
              {/* Title hidden on mobile — the grabber's logo conveys the same
                "this is the ballot" affordance without redundant text. */}
              <Typography
                component="h1"
                fontWeight="bold"
                sx={{
                  fontSize: headerTitleSize,
                  fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 1.3,
                  // Hidden on mobile, so the color value only takes
                  // effect on desktop where the wrapper is white.
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {t('header.title')}
              </Typography>
              {/* County/locality beneath the title, desktop-only; on mobile the locality
                lives in the BallotSheet's teal band. */}
              {localityLabel && (
                <Box
                  sx={{
                    // xs: 'none' hides on mobile (locality shows in the teal band). Desktop
                    // mirrors main: 16px icon + text, slight negative margin to optically
                    // align the icon with the title above.
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 0.5,
                    ml: '-4px',
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      color: primitiveColors.black,
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  />
                  <Typography
                    sx={{
                      fontSize: 16,
                      lineHeight: 1.3,
                      fontFamily: "'DM Sans', sans-serif",
                      color: primitiveColors.black,
                    }}
                  >
                    {localityLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Ballot status control (desktop): condensed dot + chevron on the
            header row, right of the title. Hidden in national mode (no ballot
            record to describe). On mobile the equivalent lives in the sheet's
            teal band (see BallotSheet / BallotGrabber), so it shows even when
            the sheet is collapsed. */}
          {!isNational && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexShrink: 0 }}>
              <BallotStatusButton condensed />
            </Box>
          )}

          {/* No standalone mobile close button — the bottom sheet now
            handles close via the backdrop tap and the grabber drag. */}
        </Box>

        {/* Address Row */}
        <Box
          ref={addressAreaRef}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: 1,
            py: 1,
          }}
        >
          {/* Address input is interactive on every ballot. Saving a new address adds a new
            ballot (handleSaveBallotAddress) without altering the viewed one; the value
            tracks whichever ballot is on screen via the viewedRecord → ballotAddress sync. */}
          {
            <>
              {/* Address input is always visible — in national mode it's the path into the
              personalized ballot. Authed users save via /user/settings; guests trigger
              sign-in, which flips them into personal mode via canLoadRealBallot. */}
              <Box
                // Capture-phase handlers fire before the input's own focus logic, so the
                // sheet snaps to mid before the keyboard opens — otherwise mobile browsers
                // scroll the input into view using the smaller pre-snap geometry.
                onClickCapture={handleAddressInputInteract}
                onFocusCapture={handleAddressInputInteract}
                sx={{
                  flex: 1,
                  minWidth: 180,
                  opacity: addressTransitioning ? 0 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <AutoCompleteAddressInput
                  value={ballotAddress}
                  onChange={handleBallotAddressChange}
                  onAddressSelect={handleBallotAddressSelectPending}
                  onClear={() => {
                    analytics.track('Sample Ballot Address Cleared', {
                      had_saved_address: savedAddress.trim().length > 0,
                    });
                    // Wipe the visible text and any half-completed autocomplete selection;
                    // leaves the saved address alone ("Back to my ballot"/re-save handle that).
                    handleBallotAddressChange('');
                    setPendingAddressDetails(null);
                  }}
                  placeholder={t('address.placeholder')}
                  size="small"
                  sx={{
                    // Override getAutoCompleteAddressInputStyles' white wrapper bg so the
                    // input sits directly on the teal band, not in a white rectangle.
                    background: 'transparent',
                    // 16px is iOS Safari's no-auto-zoom threshold — smaller inputs trigger a
                    // viewport zoom on focus. Matches the chat input's bump.
                    '& .MuiOutlinedInput-root': {
                      borderRadius: BORDER_RADIUS,
                      fontSize: '16px',
                      backgroundColor: primitiveColors.white,
                    },
                  }}
                />
              </Box>
              {pendingAddressDetails && (
                <>
                  {/* Polite live-region so screen-reader users are told Save/Cancel
                  appeared, rather than discovering them only by Tab-ing past the input. */}
                  <Box
                    aria-live="polite"
                    sx={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      p: 0,
                      m: -1,
                      overflow: 'hidden',
                      clip: 'rect(0 0 0 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                  >
                    {t('address.liveAnnouncement')}
                  </Box>
                  <Button
                    onClick={handleSaveBallotAddress}
                    size="small"
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      borderRadius: BORDER_RADIUS,
                      py: '4px',
                      backgroundColor: semanticColors.textPrimary,
                      boxShadow: 'none',
                      flexShrink: 0,
                      '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
                      '&:active': { boxShadow: 'none' },
                    }}
                  >
                    {t('address.save')}
                  </Button>
                  <Button
                    onClick={revertAddress}
                    size="small"
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      borderRadius: BORDER_RADIUS,
                      py: '4px',
                      color: semanticColors.textSecondary,
                      flexShrink: 0,
                      '&:hover': { background: semanticColors.overlaySubtle },
                    }}
                  >
                    {t('address.cancel')}
                  </Button>
                </>
              )}
              {/* Cleared-input affordance: when a user with a saved address
                (signed-in via Cosmos or guest via localStorage) clears their
                address-bar input, surface a button that flips to the
                national view without touching the saved address. */}
              {!pendingAddressDetails &&
                !forceNationalView &&
                ballotAddress.trim() === '' &&
                savedAddress.trim() !== '' && (
                  <Button
                    onClick={() => {
                      analytics.track('Sample Ballot National View Entered');
                      setForceNationalView(true);
                    }}
                    size="small"
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      borderRadius: BORDER_RADIUS,
                      py: '4px',
                      backgroundColor: semanticColors.textPrimary,
                      boxShadow: 'none',
                      flexShrink: 0,
                      '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
                      '&:active': { boxShadow: 'none' },
                    }}
                  >
                    {t('address.seeAllContests')}
                  </Button>
                )}
              {/* Back-link from forced-national view: one-click return to the personalized
                ballot without re-typing. Clears any pending new-address entry so Save/Cancel
                disappear and a later Save can't resurrect the abandoned address. */}
              {forceNationalView && savedAddress.trim() !== '' && (
                <Button
                  onClick={() => {
                    analytics.track('Sample Ballot Personal View Restored');
                    setForceNationalView(false);
                    handleBallotAddressChange(savedAddress);
                    setPendingAddressDetails(null);
                  }}
                  size="small"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    borderRadius: BORDER_RADIUS,
                    py: '4px',
                    backgroundColor: semanticColors.textPrimary,
                    boxShadow: 'none',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
                    '&:active': { boxShadow: 'none' },
                  }}
                >
                  {t('address.backToMyBallot')}
                </Button>
              )}
            </>
          }
        </Box>
      </Box>

      {/* Ballot Tabs — Voting Info + Election tabs. National mode renders two fixed
          tabs (Primary & Special, General) instead of one per election; the panel
          filters by category. In the mobile tab model the Voting Info tab is the
          bottom bar's My Info tab instead: the Ballot section lists election tabs
          only (``mobileSection === 'ballot'``), and the My Info section has no strip
          at all (``mobileSection === 'info'`` skips this block). */}
      {mobileSection !== 'info' &&
        initialBallot.elections &&
        Array.isArray(initialBallot.elections) &&
        initialBallot.elections.length > 0 &&
        (() => {
          // tabEntries is lifted to a component-scope useMemo so the analytics effects
          // can read the tab list; this IIFE just consumes it.
          const hasInfoTab = !mobileSection;
          const infoOffset = hasInfoTab ? 1 : 0;
          return (
            <Box
              role="tablist"
              aria-label={t('tabs.ariaLabel')}
              onKeyDown={(e: React.KeyboardEvent) => {
                // Visual order: voting info (0, desktop only), then election tabs
                const electionCount = tabEntries.length;
                const totalTabs = electionCount + infoOffset;
                const currentIndex = votingInfoTabActive ? 0 : highlightedElectionTab + infoOffset;
                let next: number;
                if (e.key === 'ArrowRight') {
                  next = (currentIndex + 1) % totalTabs;
                } else if (e.key === 'ArrowLeft') {
                  next = (currentIndex - 1 + totalTabs) % totalTabs;
                } else if (e.key === 'Home') {
                  next = 0;
                } else if (e.key === 'End') {
                  next = totalTabs - 1;
                } else {
                  return;
                }
                e.preventDefault();
                if (hasInfoTab && next === 0) {
                  // Stamp the next Tab Selected event as keyboard-driven.
                  nextTabSelectionViaRef.current = 'keyboard';
                  setVotingInfoTabActive(true);
                } else {
                  handleElectionTabSelect(next - infoOffset, 'keyboard');
                }
                const nextTab = (e.currentTarget as HTMLElement).querySelector(
                  `[data-tab-index="${next}"]`
                ) as HTMLElement | null;
                nextTab?.focus();
              }}
              sx={{
                display: 'flex',
                minHeight: 36,
                backgroundColor: primitiveColors.blue1,
              }}
            >
              {/* Voting Info tab — desktop / draggable-sheet only. In the tab model
                  the Voting Info section is its own bottom tab, so this strip lists
                  election tabs only. */}
              {hasInfoTab && (
                <ButtonBase
                  id="voting-info-tab"
                  data-tab-index={0}
                  role="tab"
                  aria-selected={votingInfoTabActive}
                  aria-controls="voting-info-panel"
                  tabIndex={votingInfoTabActive ? 0 : -1}
                  onClick={() => setVotingInfoTabActive(true)}
                  disableRipple
                  aria-label={t('tabs.votingInfo')}
                  sx={{
                    minHeight: 36,
                    minWidth: 36,
                    py: 0.5,
                    px: 1,
                    borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                    color: votingInfoTabActive ? semanticColors.textPrimary : primitiveColors.black,
                    backgroundColor: votingInfoTabActive ? semanticColors.themeAqua : 'transparent',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:focus-visible': {
                      outline: `2px solid ${primitiveColors.logoPurple}`,
                      outlineOffset: '-2px',
                      zIndex: 1,
                    },
                    '&:hover': votingInfoTabActive
                      ? {}
                      : {
                          backgroundColor: primitiveColors.gray28,
                          color: primitiveColors.white,
                        },
                  }}
                >
                  <CustomInfoShieldIcon size={20} />
                </ButtonBase>
              )}

              {/* Election tabs */}
              {tabEntries.map((entry, i) => {
                const tabLabel = entry.label;
                const isSelected = !votingInfoTabActive && highlightedElectionTab === i;
                // Show left divider when neither this tab nor its left neighbor is selected
                const leftNeighborSelected =
                  i === 0
                    ? hasInfoTab && votingInfoTabActive
                    : !votingInfoTabActive && highlightedElectionTab === i - 1;
                const showDivider = !isSelected && !leftNeighborSelected;
                return (
                  <ButtonBase
                    key={entry.key}
                    id={`election-tab-${i}`}
                    data-tab-index={i + infoOffset}
                    role="tab"
                    aria-selected={isSelected}
                    aria-controls={`election-panel-${i}`}
                    tabIndex={isSelected ? 0 : -1}
                    onClick={() => handleElectionTabSelect(i, 'click')}
                    disableRipple
                    sx={{
                      minHeight: 36,
                      py: 0.5,
                      px: 1.25,
                      textTransform: 'none',
                      fontSize: { lg: 13, xs: 12 },
                      fontWeight: 600,
                      fontFamily: "'DM Sans', sans-serif",
                      borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                      color: isSelected ? primitiveColors.white : primitiveColors.black,
                      backgroundColor: isSelected
                        ? semanticColors.ballotElectionCard
                        : 'transparent',
                      transition: 'background-color 0.2s ease, color 0.2s ease, flex 0.3s ease',
                      flex: isSelected ? '0 0 auto' : '0 1 auto',
                      minWidth: 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      justifyContent: 'flex-start',
                      position: 'relative',
                      '&::before': showDivider
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '20%',
                            bottom: '20%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                          }
                        : { display: 'none' },
                      '&:focus-visible': {
                        outline: `2px solid ${primitiveColors.logoPurple}`,
                        outlineOffset: '-2px',
                        zIndex: 1,
                      },
                      '&:hover': isSelected
                        ? {}
                        : {
                            backgroundColor: primitiveColors.gray28,
                            color: primitiveColors.white,
                          },
                    }}
                  >
                    {tabLabel}
                  </ButtonBase>
                );
              })}
            </Box>
          );
        })()}

      {/* Voting Information Panel */}
      {votingInfoTabActive && (
        <Box
          // In the tab model this section is the bottom bar's My Info tab, with no
          // in-panel tab strip — so it is plain content there, not a tabpanel
          // labelled by a tab that does not exist. Desktop keeps the tabpanel role.
          role={mobileSection ? undefined : 'tabpanel'}
          id="voting-info-panel"
          aria-labelledby={mobileSection ? undefined : 'voting-info-tab'}
          sx={{
            flex: 1,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            // Contain overscroll so iOS doesn't chain into a page scroll of the chat
            // beneath, and the rubber-band bounce doesn't break the sheet-drag handoff.
            overscrollBehavior: 'contain',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Local races notice for Ballotpedia-gap states, at the top of the Voting
              Info tab. Gates on the rendered state (profile or guest address); hidden
              in national view. */}
            {currentBallotState && BALLOTPEDIA_GAP_STATES.has(currentBallotState) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 1,
                  backgroundColor: semanticColors.themeAqua,
                  borderRadius: BORDER_RADIUS,
                  // Square the top-left so the aqua notice meets the active
                  // info tab flush (no rounded-corner sliver).
                  borderTopLeftRadius: 0,
                }}
                role="status"
                aria-label={t('votingInfo.localRaces.ariaLabel')}
              >
                <InfoOutlinedIcon
                  sx={{ color: semanticColors.ballotElectionCard, flexShrink: 0 }}
                  fontSize="small"
                  aria-hidden="true"
                />
                <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.4 }}>
                  {t('votingInfo.localRaces.text')}{' '}
                  <Link
                    component="button"
                    type="button"
                    sx={{
                      color: semanticColors.textPrimary,
                      fontWeight: 600,
                      verticalAlign: 'baseline',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                      '&:focus-visible': {
                        outline: `2px solid ${primitiveColors.logoPurple}`,
                        outlineOffset: '2px',
                      },
                    }}
                    onClick={() => {
                      // Already on the info tab — scroll to and highlight the
                      // election offices via the shared pending-highlight effect.
                      setPendingElectionOfficeHighlight((c) => c + 1);
                    }}
                  >
                    {t('votingInfo.localRaces.checkLink')}
                  </Link>{' '}
                  {t('votingInfo.localRaces.textSuffix')}
                </Typography>
              </Box>
            )}
            {/* Election Information Sources Accordion */}
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              slotProps={{
                transition: { unmountOnExit: true },
              }}
              sx={{
                backgroundColor: semanticColors.themeAqua,
                borderRadius: BORDER_RADIUS,
                // Square the top-left only when this accordion is the first
                // card (no notice above it) so it meets the info tab flush.
                borderTopLeftRadius: localRacesNoticeShown ? BORDER_RADIUS : 0,
                '&::before': { display: 'none' },
                '&.MuiPaper-root': {
                  borderRadius: BORDER_RADIUS,
                  borderTopLeftRadius: localRacesNoticeShown ? BORDER_RADIUS : 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: semanticColors.textPrimary }} />}
                sx={{
                  ...votingInfoSectionHeaderSx,
                  borderTopLeftRadius: localRacesNoticeShown
                    ? `${BORDER_RADIUS} !important`
                    : '0 !important',
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight="bold"
                  lineHeight={1.1}
                  sx={{ fontSize: { xs: 14, lg: 16 } }}
                >
                  {t('votingInfo.infoSources.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 0,
                  backgroundColor: semanticColors.surface,
                  borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: { xs: 12, lg: 13 } }}>
                    {t('votingInfo.infoSources.sourcedPrefix')}
                    <Link
                      href="https://www.democracy.works"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('opensInNewTab', {
                        label: t('votingInfo.infoSources.democracyWorksLabel'),
                      })}
                      onClick={() =>
                        analytics.track('Sample Ballot Voting Info Link Clicked', {
                          link_type: 'info_source',
                          link_label: 'Democracy Works',
                          link_url: 'https://www.democracy.works',
                        })
                      }
                      sx={{ color: semanticColors.brandPrimaryHover, fontWeight: 600 }}
                    >
                      {t('votingInfo.infoSources.democracyWorksLabel')}
                    </Link>
                    {t('votingInfo.infoSources.sourcedConjunction')}
                    <Link
                      href="https://www.ballotpedia.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('opensInNewTab', {
                        label: t('votingInfo.infoSources.ballotpediaLabel'),
                      })}
                      onClick={() =>
                        analytics.track('Sample Ballot Voting Info Link Clicked', {
                          link_type: 'info_source',
                          link_label: 'Ballotpedia',
                          link_url: 'https://www.ballotpedia.org',
                        })
                      }
                      sx={{ color: semanticColors.brandPrimaryHover, fontWeight: 600 }}
                    >
                      {t('votingInfo.infoSources.ballotpediaLabel')}
                    </Link>
                    {t('votingInfo.infoSources.sourcedSuffix')}
                  </Typography>
                  {/* Election offices are the source of truth, so the disclaimer
                      links them directly. The local-races notice flashes this
                      line, which is why it carries the highlight ref. */}
                  <Typography
                    ref={electionOfficesRef}
                    sx={{
                      fontSize: { xs: 12, lg: 13 },
                      mt: 1,
                      // Negative margin lets the flash bleed past the text
                      // without reflowing the sentence when it animates in.
                      mx: -0.5,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: BORDER_RADIUS,
                      backgroundColor: electionOfficeHighlight
                        ? semanticColors.themeAqua
                        : 'transparent',
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    {officeGroups.length === 0 ? (
                      t('votingInfo.infoSources.officialSourceFallback')
                    ) : (
                      <>
                        {t('votingInfo.infoSources.officialSourceLead')}
                        {officeGroups.map((group, groupIdx) => (
                          <React.Fragment key={group.name}>
                            {groupIdx > 0 ? ', ' : ''}
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {dir === 'rtl' ? isolate(group.name) : group.name}
                            </Box>
                            {' ('}
                            {group.links.map((link, linkIdx) => (
                              <React.Fragment key={link.url}>
                                {linkIdx > 0 ? ' · ' : ''}
                                <Link
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t('opensInNewTab', { label: link.label })}
                                  onClick={() =>
                                    analytics.track('Sample Ballot Voting Info Link Clicked', {
                                      link_type: 'election_office',
                                      link_label: link.label,
                                      link_url: link.url,
                                      office_name: group.name,
                                      office_level: group.level,
                                    })
                                  }
                                  sx={{ color: semanticColors.brandPrimaryHover, fontWeight: 600 }}
                                >
                                  {shortOfficeLabel(link)}
                                </Link>
                              </React.Fragment>
                            ))}
                            {')'}
                          </React.Fragment>
                        ))}
                        {'.'}
                      </>
                    )}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Voting Dates Accordion */}
            {initialBallot.elections &&
              Array.isArray(initialBallot.elections) &&
              initialBallot.elections.length > 0 && (
                <Accordion
                  defaultExpanded
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: semanticColors.themeAqua,
                    borderRadius: BORDER_RADIUS,
                    '&::before': { display: 'none' },
                    '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: semanticColors.textPrimary }} />}
                    sx={votingInfoSectionHeaderSx}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      fontWeight="bold"
                      lineHeight={1.1}
                      sx={{ fontSize: { xs: 14, lg: 16 } }}
                    >
                      {t('votingInfo.votingDates.title')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 1,
                      backgroundColor: semanticColors.surface,
                      borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {(initialBallot as BallotUser).elections.map(
                      (election: Election, elIdx: number) => {
                        const vd = election.voting_dates;
                        if (!vd || Object.keys(vd).length === 0) return null;

                        const electionTitle = election.election_date
                          ? `${fmtDate(election.election_date, locale)} — ${election.election_name || t('elections.fallbackName')}`
                          : election.election_name || t('elections.fallbackName');

                        // Build registration deadline rows, grouping same-date methods
                        const regRows: Array<{ label: string; value: string }> = [];
                        const regOnline = vd.registration_online_date;
                        const regMail = vd.registration_mail_date;
                        const regInPerson = vd.registration_in_person_date;
                        if (regOnline && regOnline === regMail && regMail === regInPerson) {
                          regRows.push({
                            label: t('votingInfo.votingDates.registrationDeadline'),
                            value: fmtShort(regOnline, locale),
                          });
                        } else {
                          if (regOnline)
                            regRows.push({
                              label: t('votingInfo.votingDates.registerOnlineBy'),
                              value: fmtShort(regOnline, locale),
                            });
                          if (regMail) {
                            const suffix =
                              vd.registration_mail_postmarked_or_received === 'postmarked'
                                ? t('votingInfo.votingDates.postmarked')
                                : vd.registration_mail_postmarked_or_received === 'received'
                                  ? t('votingInfo.votingDates.receivedBy')
                                  : '';
                            regRows.push({
                              label: t('votingInfo.votingDates.registerByMailBy'),
                              value: `${fmtShort(regMail, locale)}${suffix}`,
                            });
                          }
                          if (regInPerson)
                            regRows.push({
                              label: t('votingInfo.votingDates.registerInPersonBy'),
                              value: fmtShort(regInPerson, locale),
                            });
                        }
                        if (vd.registration_same_day) {
                          regRows.push({
                            label: t('votingInfo.votingDates.sameDayRegistration'),
                            value: t('votingInfo.votingDates.availableAtPollingPlace'),
                          });
                        }

                        // Early voting row
                        const earlyRows: Array<{ label: string; value: string }> = [];
                        if (vd.early_voting_start || vd.early_voting_end) {
                          const range =
                            vd.early_voting_start && vd.early_voting_end
                              ? `${fmtShort(vd.early_voting_start, locale)} – ${fmtShort(vd.early_voting_end, locale)}`
                              : vd.early_voting_start
                                ? t('votingInfo.votingDates.starting', {
                                    date: fmtShort(vd.early_voting_start, locale),
                                  })
                                : vd.early_voting_end
                                  ? t('votingInfo.votingDates.through', {
                                      date: fmtShort(vd.early_voting_end, locale),
                                    })
                                  : '';
                          earlyRows.push({
                            label: t('votingInfo.votingDates.earlyVoting'),
                            value:
                              range +
                              (vd.early_voting_varies
                                ? t('votingInfo.votingDates.mayVaryByCounty')
                                : ''),
                          });
                        }

                        // Absentee / mail ballot rows
                        const absenteeRows: Array<{ label: string; value: string }> = [];
                        if (vd.absentee_request_date) {
                          absenteeRows.push({
                            label: t('votingInfo.votingDates.requestAbsenteeBallotBy'),
                            value: fmtShort(vd.absentee_request_date, locale),
                          });
                        }
                        if (vd.all_mail_send_start) {
                          absenteeRows.push({
                            label: t('votingInfo.votingDates.mailBallotsSentStarting'),
                            value: fmtShort(vd.all_mail_send_start, locale),
                          });
                        }
                        if (vd.absentee_return_mail_date) {
                          const suffix =
                            vd.absentee_postmarked_or_received === 'postmarked'
                              ? t('votingInfo.votingDates.postmarked')
                              : vd.absentee_postmarked_or_received === 'received'
                                ? t('votingInfo.votingDates.receivedBy')
                                : '';
                          absenteeRows.push({
                            label: t('votingInfo.votingDates.returnBallotByMailBy'),
                            value: `${fmtShort(vd.absentee_return_mail_date, locale)}${suffix}`,
                          });
                        }
                        if (vd.absentee_return_in_person_date) {
                          absenteeRows.push({
                            label: t('votingInfo.votingDates.returnBallotInPersonBy'),
                            value: fmtShort(vd.absentee_return_in_person_date, locale),
                          });
                        }

                        // Polls close
                        const pollsRow = vd.polls_close_time
                          ? {
                              label: t('votingInfo.votingDates.pollsClose'),
                              value: fmtTime(vd.polls_close_time, locale),
                            }
                          : null;

                        const allRows = [
                          ...regRows,
                          ...earlyRows,
                          ...absenteeRows,
                          ...(pollsRow ? [pollsRow] : []),
                        ];
                        if (allRows.length === 0) return null;

                        // Build up to 3 summary rows: registration online (→ mail →
                        // deadline), same-day (→ in-person), and early voting if available.
                        const summaryRows: Array<{ label: string; value: string }> = [];
                        // Row 1: registration online, fall back to mail
                        const onlineRow = regRows.find(
                          (r) => r.label === t('votingInfo.votingDates.registerOnlineBy')
                        );
                        const mailRow = regRows.find(
                          (r) => r.label === t('votingInfo.votingDates.registerByMailBy')
                        );
                        const deadlineRow = regRows.find(
                          (r) => r.label === t('votingInfo.votingDates.registrationDeadline')
                        );
                        if (onlineRow) summaryRows.push(onlineRow);
                        else if (mailRow) summaryRows.push(mailRow);
                        else if (deadlineRow) summaryRows.push(deadlineRow);
                        // Row 2: same-day registration, fall back to in-person
                        const sameDayRow = regRows.find(
                          (r) => r.label === t('votingInfo.votingDates.sameDayRegistration')
                        );
                        const inPersonRow = regRows.find(
                          (r) => r.label === t('votingInfo.votingDates.registerInPersonBy')
                        );
                        if (sameDayRow) summaryRows.push(sameDayRow);
                        else if (inPersonRow) summaryRows.push(inPersonRow);
                        // Row 3: early voting (if available)
                        if (earlyRows.length > 0) summaryRows.push(earlyRows[0]);

                        const isExpanded = votingDatesExpanded[elIdx] ?? false;
                        // Default only the first election's date header open; collapse
                        // the rest. User toggles are preserved in votingDatesAccordionOpen.
                        const isAccordionOpen = votingDatesAccordionOpen[elIdx] ?? elIdx === 0;
                        // Only offer the Show All / Show Less toggle when the
                        // full list has more rows than the curated summary.
                        const hasMore = allRows.length > summaryRows.length;
                        const rowsToShow = isExpanded ? allRows : summaryRows;

                        // Shared row rendering helper
                        const renderRow = (
                          row: { label: string; value: string },
                          rowIdx: number,
                          showTopBorder: boolean
                        ) => (
                          <Box
                            key={`${row.label}-${rowIdx}`}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'baseline',
                              gap: 2,
                              px: 2,
                              py: 1,
                              borderTop: showTopBorder
                                ? `1px solid ${primitiveColors.gray28}`
                                : 'none',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: 12, lg: 13 },
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {row.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: 12, lg: 13 },
                                textAlign: 'right',
                              }}
                            >
                              {row.value}
                            </Typography>
                          </Box>
                        );

                        return (
                          <Accordion
                            key={election.election_date ?? `vd-election-${elIdx}`}
                            expanded={isAccordionOpen}
                            onChange={() => {
                              // Toggle accordion open/closed; preserve expand state across toggles
                              setVotingDatesAccordionOpen((prev) => ({
                                ...prev,
                                [elIdx]: !isAccordionOpen,
                              }));
                            }}
                            disableGutters
                            elevation={0}
                            sx={{
                              '&::before': { display: 'none' },
                              borderRadius: BORDER_RADIUS,
                              '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                            }}
                          >
                            <AccordionSummary
                              aria-label={electionTitle}
                              expandIcon={
                                <ExpandMoreIcon
                                  sx={{
                                    color: semanticColors.textOnPrimary,
                                    transform: isAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                  }}
                                />
                              }
                              sx={{
                                backgroundColor: semanticColors.ballotElectionCard,
                                color: semanticColors.textOnPrimary,
                                borderRadius: BORDER_RADIUS,
                                '&.Mui-expanded': {
                                  borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                                },
                                p: 1,
                                minHeight: 'unset',
                                '& .MuiAccordionSummary-content': { my: 0, pr: 3 },
                                '& .MuiAccordionSummary-expandIconWrapper': (t) => ({
                                  position: 'absolute',
                                  right: t.spacing(1),
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  '&.Mui-expanded': {
                                    transform: 'translateY(-50%)',
                                  },
                                }),
                              }}
                            >
                              <Typography
                                variant="h6"
                                component="h3"
                                fontWeight="bold"
                                lineHeight={1.1}
                                sx={{ fontSize: { lg: 14, xs: 12 } }}
                              >
                                {electionTitle}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                p: 0,
                                backgroundColor: semanticColors.surface,
                                borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                              }}
                            >
                              {/* With more rows than the summary, the whole body is an
                                expand/collapse toggle with a "Show All"/"Show Less" label
                                (mirrors mobile); otherwise rows are static content. */}
                              {hasMore ? (
                                <ButtonBase
                                  disableRipple
                                  aria-label={
                                    isExpanded
                                      ? t('votingInfo.votingDates.showFewerAriaLabel')
                                      : t('votingInfo.votingDates.showAllAriaLabel', {
                                          count: allRows.length,
                                        })
                                  }
                                  aria-expanded={isExpanded}
                                  onClick={() =>
                                    setVotingDatesExpanded((prev) => ({
                                      ...prev,
                                      [elIdx]: !isExpanded,
                                    }))
                                  }
                                  sx={{
                                    width: '100%',
                                    display: 'block',
                                    textAlign: 'left',
                                    borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                                    '&:hover': {
                                      backgroundColor: semanticColors.surfaceWarm,
                                    },
                                    '&:focus-visible': {
                                      outline: `2px solid ${primitiveColors.logoPurple}`,
                                      outlineOffset: '-2px',
                                      zIndex: 1,
                                    },
                                  }}
                                >
                                  {rowsToShow.map((row, idx) => renderRow(row, idx, idx > 0))}
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 0.5,
                                      borderTop: `1px solid ${primitiveColors.gray28}`,
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: { xs: 12, lg: 13 },
                                        fontWeight: 600,
                                        color: semanticColors.brandPrimary,
                                      }}
                                    >
                                      {isExpanded
                                        ? t('votingInfo.votingDates.showLess')
                                        : t('votingInfo.votingDates.showAll')}
                                    </Typography>
                                  </Box>
                                </ButtonBase>
                              ) : (
                                <Box>
                                  {allRows.map((row, rowIdx) => renderRow(row, rowIdx, rowIdx > 0))}
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      }
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            {/* Your Officeholders Accordion — current elected officials for the voter's
                address (Ballotpedia current_officeholders). Shown for any enriched ballot,
                hidden when the array is empty (national view or an unenriched ballot). */}
            {Array.isArray((initialBallot as BallotUser).current_officeholders) &&
              (initialBallot as BallotUser).current_officeholders!.length > 0 && (
                <Accordion
                  defaultExpanded
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: semanticColors.themeAqua,
                    borderRadius: BORDER_RADIUS,
                    '&::before': { display: 'none' },
                    '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: semanticColors.textPrimary }} />}
                    sx={votingInfoSectionHeaderSx}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      fontWeight="bold"
                      lineHeight={1.1}
                      sx={{ fontSize: { xs: 14, lg: 16 } }}
                    >
                      {t('votingInfo.officeholders.title')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 1,
                      backgroundColor: semanticColors.surface,
                      borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {groupOfficeholders((initialBallot as BallotUser).current_officeholders!).map(
                      (levelGroup) => (
                        // Level bucket (Federal → State → Local) — collapsible,
                        // styled like the Voting Dates election sub-header.
                        <Accordion
                          key={levelGroup.level}
                          defaultExpanded
                          disableGutters
                          elevation={0}
                          sx={{
                            '&::before': { display: 'none' },
                            borderRadius: BORDER_RADIUS,
                            '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon sx={{ color: semanticColors.textOnPrimary }} />
                            }
                            sx={{
                              backgroundColor: semanticColors.ballotElectionCard,
                              color: semanticColors.textOnPrimary,
                              borderRadius: BORDER_RADIUS,
                              '&.Mui-expanded': {
                                borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                              },
                              p: 1,
                              minHeight: 'unset',
                              '& .MuiAccordionSummary-content': { my: 0, pr: 3 },
                              // Take the chevron out of flow (absolute, margin-centered) so
                              // header height is text-driven, matching the Voting Dates
                              // sub-header. MUI's default rotate still applies.
                              '& .MuiAccordionSummary-expandIconWrapper': {
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                marginTop: '-12px',
                              },
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="h3"
                              fontWeight="bold"
                              lineHeight={1.1}
                              sx={{ fontSize: { lg: 14, xs: 12 } }}
                            >
                              {t(
                                `votingInfo.officeholders.levels.${levelGroup.level.toLowerCase()}`,
                                { defaultValue: levelGroup.level }
                              )}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              p: 1,
                              backgroundColor: primitiveColors.blue1,
                              borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            {levelGroup.branches.map((branchGroup) => (
                              // Branch sub-bucket (Executive/Legislative/Judicial), lighter
                              // than the level header. Noisy local legislative/judicial bodies
                              // start collapsed; executives and higher levels start open.
                              <Accordion
                                key={`${levelGroup.level}-${branchGroup.branch}`}
                                defaultExpanded={
                                  !(
                                    levelGroup.level.toLowerCase() === 'local' &&
                                    ['legislative', 'judicial'].includes(
                                      branchGroup.branch.toLowerCase()
                                    )
                                  )
                                }
                                disableGutters
                                elevation={0}
                                sx={{
                                  '&::before': { display: 'none' },
                                  borderRadius: BORDER_RADIUS,
                                  overflow: 'hidden',
                                  '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={
                                    <ExpandMoreIcon sx={{ color: semanticColors.textOnPrimary }} />
                                  }
                                  sx={{
                                    backgroundColor: semanticColors.ballotOfficeHeader,
                                    color: semanticColors.textOnPrimary,
                                    borderRadius: BORDER_RADIUS,
                                    '&.Mui-expanded': {
                                      borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                                    },
                                    p: 1,
                                    minHeight: 'unset',
                                    '& .MuiAccordionSummary-content': { my: 0, pr: 3 },
                                    '& .MuiAccordionSummary-expandIconWrapper': {
                                      position: 'absolute',
                                      right: 8,
                                      top: '50%',
                                      marginTop: '-12px',
                                    },
                                  }}
                                >
                                  <Typography
                                    component="h4"
                                    fontWeight="bold"
                                    lineHeight={1.1}
                                    sx={{ fontSize: { xs: 12, lg: 14 } }}
                                  >
                                    {t(
                                      `votingInfo.officeholders.branches.${branchGroup.branch.toLowerCase()}`,
                                      { defaultValue: branchGroup.branch }
                                    )}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                  sx={{
                                    // Match the candidate list's px:1 inset so each
                                    // officeholder row's expand arrow lines up with theirs.
                                    px: 1,
                                    py: 0,
                                    backgroundColor: semanticColors.surface,
                                    borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                                  }}
                                >
                                  {branchGroup.officeholders.map((oh, ohIdx) => {
                                    const partyAbbr = abbreviateParty(oh.party);
                                    const officeholderActionState = getBallotActionState({
                                      kind: 'fullProfile',
                                      id: oh.name,
                                      pendingAction,
                                      isProcessing,
                                    });
                                    const hasProfile =
                                      Boolean(oh.url) && isSafeHttpUrl(oh.url ?? '');
                                    const contactLinks = (oh.contacts ?? [])
                                      .map((contact) => ({
                                        contact,
                                        href: officeholderContactHref(contact),
                                      }))
                                      .filter(
                                        (
                                          c
                                        ): c is {
                                          contact: OfficeholderContact;
                                          href: string;
                                        } => Boolean(c.href)
                                      )
                                      // Fixed order: website, email, phone.
                                      .sort(
                                        (a, b) =>
                                          contactOrderIndex(a.contact.type) -
                                          contactOrderIndex(b.contact.type)
                                      );
                                    return (
                                      <Accordion
                                        key={`${oh.name}-${ohIdx}`}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                          backgroundColor: 'transparent',
                                          '&::before': { display: 'none' },
                                          '&.MuiPaper-root': { borderRadius: 0 },
                                          // Divider between rows only; the first row sits flush
                                          // under the branch header (no inset top line).
                                          borderTop:
                                            ohIdx === 0
                                              ? 'none'
                                              : `1px solid ${primitiveColors.gray28}`,
                                        }}
                                      >
                                        {/* Row expansion is intentionally not tracked in
                                            Mixpanel (product decision). */}
                                        <AccordionSummary
                                          expandIcon={
                                            <ExpandMoreIcon
                                              sx={{
                                                fontSize: 18,
                                                color: semanticColors.textMediumEmphasis,
                                              }}
                                            />
                                          }
                                          sx={{
                                            px: 2,
                                            minHeight: 'unset',
                                            '& .MuiAccordionSummary-content': {
                                              my: 1,
                                              display: 'flex',
                                              alignItems: 'flex-start',
                                              justifyContent: 'space-between',
                                              gap: 1,
                                              pr: 3,
                                            },
                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                              position: 'absolute',
                                              right: 8,
                                              top: '50%',
                                              marginTop: '-9px',
                                            },
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: { xs: 13, lg: 14 },
                                              fontWeight: 600,
                                              flexShrink: 0,
                                            }}
                                          >
                                            {dir === 'rtl' ? isolate(oh.name) : oh.name}
                                            {partyAbbr ? ` (${partyAbbr})` : ''}
                                          </Typography>
                                          {oh.office_name && (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: { xs: 13, lg: 14 },
                                                fontWeight: 600,
                                                textAlign: 'right',
                                                minWidth: 0,
                                                overflowWrap: 'break-word',
                                              }}
                                            >
                                              {oh.office_name}
                                            </Typography>
                                          )}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              alignItems: 'center',
                                              gap: 0.75,
                                            }}
                                          >
                                            {/* Full profile — sends "{name} full profile"
                                                to chat, matching the candidate button. The
                                                officeholder's name keys the pending action, so
                                                only the chip that was clicked reads "getting
                                                profile". */}
                                            <Chip
                                              label={
                                                officeholderActionState.isThisActionPending
                                                  ? t('votingInfo.officeholders.gettingProfile')
                                                  : t('candidate.fullProfile')
                                              }
                                              onClick={() => {
                                                if (isProcessing || pendingAction) return;
                                                setPendingAction({
                                                  kind: 'fullProfile',
                                                  id: oh.name,
                                                });
                                                analytics.track(
                                                  'Sample Ballot Officeholder Full Profile Clicked',
                                                  { link_label: oh.name }
                                                );
                                                sendChat(
                                                  t('candidate.fullProfileQuery', {
                                                    name: oh.name,
                                                  }),
                                                  {
                                                    ballotRef: {
                                                      kind: 'officeholder',
                                                      officeholder_name: oh.name,
                                                    },
                                                  }
                                                );
                                              }}
                                              disabled={officeholderActionState.isDisabled}
                                              size="small"
                                              sx={{
                                                fontSize: 11,
                                                letterSpacing: '0.19px',
                                                borderRadius: BORDER_RADIUS,
                                                border: '2px solid transparent',
                                                background: gradientBorderBackground(
                                                  semanticColors.background,
                                                  semanticGradients.primary
                                                ),
                                                backgroundColor: semanticColors.chatBotMessage,
                                                width: 'fit-content',
                                                '& .MuiChip-label': { px: 1.5 },
                                              }}
                                            />
                                            {/* Ballotpedia — "B" in logo blue, "P" in logo
                                                orange, blue border, no circle. */}
                                            {hasProfile && (
                                              <Tooltip
                                                title={t(
                                                  'votingInfo.officeholders.profileLinkAriaLabel',
                                                  { name: oh.name }
                                                )}
                                              >
                                                <IconButton
                                                  component="a"
                                                  href={oh.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  size="small"
                                                  aria-label={t(
                                                    'votingInfo.officeholders.profileLinkAriaLabel',
                                                    { name: oh.name }
                                                  )}
                                                  onClick={() =>
                                                    analytics.track(
                                                      'Sample Ballot Voting Info Link Clicked',
                                                      {
                                                        link_type: 'officeholder_profile',
                                                        link_label: oh.name,
                                                        link_url: oh.url,
                                                      }
                                                    )
                                                  }
                                                  sx={{
                                                    border: `2px solid ${semanticColors.ballotpediaBlue}`,
                                                    borderRadius: BORDER_RADIUS,
                                                    p: '2px',
                                                  }}
                                                >
                                                  <Box
                                                    aria-hidden
                                                    sx={{
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      height: 14,
                                                      fontSize: 12,
                                                      fontWeight: 700,
                                                      lineHeight: 1,
                                                      letterSpacing: 0.2,
                                                    }}
                                                  >
                                                    <Box
                                                      component="span"
                                                      sx={{
                                                        color: semanticColors.ballotpediaBlue,
                                                      }}
                                                    >
                                                      B
                                                    </Box>
                                                    <Box
                                                      component="span"
                                                      sx={{
                                                        color: semanticColors.ballotpediaOrange,
                                                      }}
                                                    >
                                                      P
                                                    </Box>
                                                  </Box>
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {/* Contact channels (Heroicons): website (gray
                                                globe), email (blue envelope), phone (black). */}
                                            {contactLinks.map(({ contact, href }, cIdx) => {
                                              const type = contact.type.toLowerCase();
                                              const ContactIcon =
                                                type === 'email'
                                                  ? EnvelopeIcon
                                                  : type === 'phone'
                                                    ? PhoneIcon
                                                    : GlobeAltIcon;
                                              const iconColor =
                                                type === 'email'
                                                  ? semanticColors.brandPrimaryHover
                                                  : type === 'phone'
                                                    ? primitiveColors.black
                                                    : semanticColors.websiteLink;
                                              const isWebsite = type === 'website';
                                              return (
                                                <Tooltip
                                                  key={`${type}-${cIdx}`}
                                                  title={t(
                                                    `votingInfo.officeholders.contact.${type}`,
                                                    { name: oh.name }
                                                  )}
                                                >
                                                  <IconButton
                                                    component="a"
                                                    href={href}
                                                    {...(isWebsite
                                                      ? {
                                                          target: '_blank',
                                                          rel: 'noopener noreferrer',
                                                        }
                                                      : {})}
                                                    size="small"
                                                    aria-label={t(
                                                      `votingInfo.officeholders.contact.${type}`,
                                                      { name: oh.name }
                                                    )}
                                                    onClick={() =>
                                                      analytics.track(
                                                        'Sample Ballot Voting Info Link Clicked',
                                                        {
                                                          link_type: `officeholder_${type}`,
                                                          link_label: oh.name,
                                                          link_url: href,
                                                        }
                                                      )
                                                    }
                                                    sx={{
                                                      border: `2px solid ${iconColor}`,
                                                      borderRadius: BORDER_RADIUS,
                                                      p: '2px',
                                                      color: iconColor,
                                                    }}
                                                  >
                                                    <ContactIcon
                                                      style={{ width: 14, height: 14 }}
                                                    />
                                                  </IconButton>
                                                </Tooltip>
                                              );
                                            })}
                                          </Box>
                                        </AccordionDetails>
                                      </Accordion>
                                    );
                                  })}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      )
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
          </Box>
        </Box>
      )}

      {/* Election Content */}
      {!votingInfoTabActive && (
        <Box
          role="tabpanel"
          id={`election-panel-${safeElectionTab}`}
          aria-labelledby={`election-tab-${safeElectionTab}`}
          sx={{
            flex: 1,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            // Contain overscroll so iOS doesn't chain into a page scroll of the chat
            // beneath, and the rubber-band bounce doesn't break the sheet-drag handoff.
            overscrollBehavior: 'contain',
          }}
        >
          {/* Ballot Races */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {initialBallot.elections &&
            Array.isArray(initialBallot.elections) &&
            initialBallot.elections.length > 0 ? (
              (() => {
                // National mode: the tab index selects a category and we render every
                // (state, election) in it. Personal mode keeps one tab per election.
                const activeCategory = isNational ? nationalTabs[safeElectionTab]?.category : null;
                const electionsToRender: Election[] = isNational
                  ? initialBallot.elections.filter((e: Election) =>
                      activeCategory ? matchesNationalCategory(e, activeCategory) : false
                    )
                  : [initialBallot.elections[safeElectionTab]];

                if (electionsToRender.length === 0) {
                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        mx: 1,
                        mt: 2,
                        textAlign: 'center',
                        backgroundColor: semanticColors.surface,
                        borderRadius: BORDER_RADIUS,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: semanticColors.textMediumEmphasis }}>
                        {activeCategory === 'general'
                          ? t('elections.noNationalGeneral')
                          : t('elections.noNationalPrimarySpecial')}
                      </Typography>
                    </Paper>
                  );
                }

                // Per-election rendering extracted for reuse by both the flat map (personal /
                // Primary & Special) and the alphabetical-group accordions (national General).
                // Closes over component-scope state (partyByElection, expandedElections, etc.).
                const renderElectionCard = (election: Election) => {
                  // Compute the per-election key + party first so the filter and toggle UI
                  // agree. Key is the synthetic id (national) or election_date (personal).
                  const electionStateKey =
                    (election as Election & { id?: string }).id ?? election.election_date;
                  const partyForThisElection =
                    partyByElection[electionStateKey] ?? PARTY_NAME_REPUBLICAN;
                  const filteredContests = filterContestsByParty(election, partyForThisElection);
                  const hasMeasures =
                    Array.isArray(election.ballot_measures) && election.ballot_measures.length > 0;

                  // DC is a federal district, not a state — surface that so labels read
                  // "DISTRICT"/"district-wide" rather than "STATE". Read state_code off the
                  // synthetic election in national mode, else the current ballot state.
                  const isDC =
                    ((election as Election & { state_code?: string }).state_code ??
                      currentBallotState) === 'DC';

                  const LEVEL_ORDER = ['federal', 'state', 'local'];
                  const levelDisplayName = (lvl: string): string => {
                    if (lvl === 'state' && isDC) return t('elections.levelDistrict');
                    return lvl.toUpperCase();
                  };
                  const otherContests = filteredContests.filter(
                    (c) => !LEVEL_ORDER.includes((c.level || '').toLowerCase())
                  );
                  const contestsByLevel = [
                    ...LEVEL_ORDER.map((lvl) => ({
                      level: lvl,
                      displayName: levelDisplayName(lvl),
                      contests: filteredContests.filter(
                        (c) => (c.level || '').toLowerCase() === lvl
                      ),
                    })).filter((group) => group.contests.length > 0),
                    ...(otherContests.length > 0
                      ? [{ level: 'other', displayName: 'OTHER', contests: otherContests }]
                      : []),
                  ];

                  const electionDateFormatted = election.election_date
                    ? fmtDate(election.election_date, locale)
                    : '';
                  const electionName = election.election_name || t('elections.fallbackName');
                  // Personal mode shows just the date (the name is already on the tab).
                  // National mode keeps the name too, since many states share one date and
                  // the tabs are category-based, so each card stays distinct for SR users.
                  const electionCardTitle = electionDateFormatted
                    ? isNational
                      ? `${electionDateFormatted} — ${electionName}`
                      : electionDateFormatted
                    : electionName;
                  const isPartisan = electionHasPartisanRaces(election);
                  // Auto-expand in personal mode (1-3 elections). National mode has 50+ per
                  // tab; expanding all on mount mounted 1,300+ BallotCandidate components and
                  // froze the UI, so default-collapse there (unmountOnExit keeps them out of DOM).
                  const isElectionExpanded = expandedElections[electionStateKey] ?? !isNational;
                  const levelHeaderBg = isPartisan
                    ? partyForThisElection === PARTY_NAME_REPUBLICAN
                      ? primitiveColors.red4
                      : primitiveColors.blue7
                    : semanticColors.ballotLevelHeader;
                  // Saturate softens the bright partisan red/blue. Nonpartisan headers use
                  // the already-muted rockSlate and skip it (the filter would grey it further).
                  const levelHeaderFilter = isPartisan ? 'saturate(70%)' : 'none';
                  // Header status chips share one full-width row (flexBasis: '100%' wrapper
                  // below) that drops beneath the date/title rather than crowding it.
                  // Ballotpedia flags this election's candidate list as still
                  // filling in (per-address, per-date). Absent → no chip.
                  const hasPendingVerificationChip = election.bp_candidate_lists_complete === false;
                  const hasMissingRacesChip = Boolean(
                    currentBallotState && BALLOTPEDIA_GAP_STATES.has(currentBallotState)
                  );
                  // The "Past" chip sits inline next to the title; the rest
                  // wrap to a full-width row below.
                  const hasStatusChips = hasPendingVerificationChip || hasMissingRacesChip;

                  // Tab mode has no Voting Info tab at the left, so the top card is flush
                  // to the panel edge — square its top-left (sheet mode rounds it there).
                  const squareTopLeft = mobileSection === 'ballot' && !isNational;
                  const cardOuterRadius = squareTopLeft
                    ? `0 ${BORDER_RADIUS} ${BORDER_RADIUS} ${BORDER_RADIUS}`
                    : BORDER_RADIUS;
                  const cardHeaderRadius = squareTopLeft
                    ? `0 ${BORDER_RADIUS} 0 0`
                    : `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`;

                  return (
                    <Box
                      key={
                        (election as Election & { id?: string }).id ??
                        election.election_date ??
                        'election'
                      }
                      sx={{ borderRadius: cardOuterRadius }}
                    >
                      <Accordion
                        expanded={isElectionExpanded}
                        onChange={(_, expanded) => {
                          setExpandedElections((prev) => ({
                            ...prev,
                            [electionStateKey]: expanded,
                          }));
                          if (expanded) trackBallotItemViewed();
                        }}
                        disableGutters
                        elevation={0}
                        slotProps={{
                          transition: { unmountOnExit: true },
                        }}
                        sx={{
                          backgroundColor: semanticColors.ballotElectionCard,
                          borderRadius: cardOuterRadius,
                          '&::before': { display: 'none' },
                          '&.MuiPaper-root': { borderRadius: cardOuterRadius },
                        }}
                      >
                        <AccordionSummary
                          aria-label={electionCardTitle}
                          expandIcon={
                            <ExpandMoreIcon sx={{ color: semanticColors.textOnPrimary }} />
                          }
                          sx={{
                            backgroundColor: semanticColors.ballotElectionCard,
                            color: semanticColors.textOnPrimary,
                            borderRadius: cardHeaderRadius,
                            p: 1,
                            minHeight: 'unset',
                            position: 'relative',
                            // Let the mobile "Ballot not finalized" Paper extend below this
                            // row without clipping; explicit overflow prevents the inherited
                            // card `overflow: hidden` from cutting it off.
                            overflow: 'visible',
                            '& .MuiAccordionSummary-content': {
                              my: 0,
                              flex: 1,
                              minWidth: 0,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              pr: 3,
                              overflow: 'visible',
                            },
                            '& .MuiAccordionSummary-expandIconWrapper': (t) => ({
                              position: 'absolute',
                              right: t.spacing(1),
                              top: '50%',
                              transform: 'translateY(-50%)',
                              '&.Mui-expanded': {
                                transform: 'translateY(-50%) rotate(180deg)',
                              },
                            }),
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="h2"
                            fontWeight="bold"
                            lineHeight={1.1}
                            sx={{
                              fontSize: { lg: 16, xs: 14 },
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            {electionCardTitle}
                          </Typography>
                          {election.is_past && (
                            <Chip
                              label={t('elections.past')}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                height: 22,
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                backgroundColor: semanticColors.warningBackground,
                                color: semanticColors.warningText,
                                border: `1px solid ${semanticColors.warningBannerBorder}`,
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          )}
                          {hasStatusChips && (
                            <Box
                              sx={{
                                flexBasis: '100%',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {hasPendingVerificationChip &&
                                (() => {
                                  // Same synthetic-id-or-date key as electionStateKey: many
                                  // states share one election_date in national mode, so keying
                                  // off date alone would open every chip for that date.
                                  const electionKey =
                                    (election as Election & { id?: string }).id ??
                                    election.election_date;
                                  const isThisChipOpen = incompletePopover?.key === electionKey;
                                  const popoverId = `incomplete-popover-${electionKey}`;
                                  const togglePopover = (e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    // Re-clicking the open chip closes it; another opens.
                                    const target = e.currentTarget;
                                    const willOpen = incompletePopover?.key !== electionKey;
                                    if (willOpen) {
                                      analytics.track('Sample Ballot Pending Verification Opened', {
                                        election_date: election.election_date,
                                      });
                                    }
                                    setIncompletePopover(
                                      willOpen ? { anchor: target, key: electionKey } : null
                                    );
                                  };
                                  return (
                                    <React.Fragment>
                                      <Chip
                                        label={t('elections.ballotPendingVerification')}
                                        size="small"
                                        // Skip `clickable`: it wraps the Chip in a ButtonBase
                                        // (<button>) nested inside the AccordionSummary <button>,
                                        // which is invalid HTML and made React's dev validator
                                        // log the JSX tree every render (a perf hit). We reproduce
                                        // the keyboard + ARIA contract manually below.
                                        role="button"
                                        tabIndex={0}
                                        aria-haspopup="dialog"
                                        aria-expanded={isThisChipOpen}
                                        aria-describedby={isThisChipOpen ? popoverId : undefined}
                                        onClick={togglePopover}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            togglePopover(
                                              e as unknown as React.MouseEvent<HTMLElement>
                                            );
                                          }
                                        }}
                                        sx={{
                                          flexShrink: 0,
                                          height: 22,
                                          fontSize: '0.6875rem',
                                          fontWeight: 600,
                                          backgroundColor: semanticColors.warningBackground,
                                          color: semanticColors.warningText,
                                          border: `1px solid ${semanticColors.warningBannerBorder}`,
                                          cursor: 'pointer',
                                          '&:hover': { backgroundColor: primitiveColors.white },
                                          '&:focus-visible': {
                                            outline: `2px solid ${semanticColors.warningText}`,
                                            outlineOffset: 1,
                                          },
                                          '& .MuiChip-label': { px: 1 },
                                        }}
                                      />
                                      <Popover
                                        open={isThisChipOpen}
                                        anchorEl={incompletePopover?.anchor ?? null}
                                        onClose={() => setIncompletePopover(null)}
                                        onClick={(e) => e.stopPropagation()}
                                        // disableRestoreFocus deliberately omitted — let MUI
                                        // return focus to the chip on close for SR users.
                                        disableAutoFocus
                                        disableEnforceFocus
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                        // 1850 puts the popover above the mobile ballot panel
                                        // (1800) and BallotSheet (1301) — MUI's default 1300 sits
                                        // below both — while staying under true modals (survey
                                        // 1900, LoginModal 2000) so their backdrops can cover it.
                                        // TODO: extract the z-index ladder (1100/1300/1800/1850/
                                        // 1900/2000) into a shared zIndices module.
                                        sx={{ zIndex: 1850 }}
                                        slotProps={{
                                          paper: {
                                            sx: {
                                              p: 2,
                                              maxWidth: 280,
                                              borderRadius: BORDER_RADIUS,
                                            },
                                          },
                                        }}
                                      >
                                        {/* id + role="tooltip" on the Typography (not the
                                            Popover root) so aria-describedby resolves to the
                                            content directly — the portal root is traversed
                                            inconsistently by screen readers. */}
                                        <Typography
                                          id={popoverId}
                                          role="tooltip"
                                          variant="body2"
                                          sx={{ lineHeight: 1.5 }}
                                        >
                                          {t('elections.pendingVerificationPopover')}
                                        </Typography>
                                      </Popover>
                                    </React.Fragment>
                                  );
                                })()}
                              {/* Mirrors the Voting Info tab's "additional local races" notice
                                for Ballotpedia-gap states, and shortcuts into that tab. */}
                              {hasMissingRacesChip && (
                                <Chip
                                  label={t('elections.someLocalRaces')}
                                  size="small"
                                  icon={<InfoOutlinedIcon />}
                                  // Skip `clickable` for the same reason as the "Ballot not
                                  // finalized" chip: a ButtonBase-wrapped Chip nested in the
                                  // AccordionSummary <button> is invalid HTML. Keyboard + ARIA
                                  // are reproduced manually.
                                  role="button"
                                  tabIndex={0}
                                  aria-label={t('elections.someLocalRacesAriaLabel')}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    analytics.track('Missing Races Chip Clicked', {
                                      state: currentBallotState,
                                    });
                                    openVotingInfoTab();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      analytics.track('Missing Races Chip Clicked', {
                                        state: currentBallotState,
                                      });
                                      openVotingInfoTab();
                                    }
                                  }}
                                  sx={{
                                    flexShrink: 0,
                                    height: 22,
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    backgroundColor: semanticColors.themeAqua,
                                    color: semanticColors.textPrimary,
                                    border: `1px solid ${semanticColors.ballotElectionCard}`,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: primitiveColors.white },
                                    '&:focus-visible': {
                                      outline: `2px solid ${semanticColors.textPrimary}`,
                                      outlineOffset: 1,
                                    },
                                    '& .MuiChip-label': { px: 1 },
                                    '& .MuiChip-icon': {
                                      fontSize: 14,
                                      color: semanticColors.textPrimary,
                                      ml: 0.75,
                                      mr: -0.25,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            p: 1,
                            backgroundColor: semanticColors.surface,
                            borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {/* Party selector — full-width segmented control under the election
                            header. Two equal buttons give a larger tap target; real buttons are
                            fine here since AccordionDetails is not itself a <button>. */}
                          {isPartisan && (
                            <Box
                              role="radiogroup"
                              aria-label={t('elections.partyAriaLabel')}
                              sx={{
                                display: 'flex',
                                // Inter-button gap gives a soft separation between halves while
                                // the selected fill still reaches the outer border; overflow:
                                // hidden clips the fills to the container's rounded corners.
                                gap: '3px',
                                height: 30,
                                borderRadius: BORDER_RADIUS,
                                border: `1px solid ${semanticColors.ballotElectionCard}`,
                                overflow: 'hidden',
                              }}
                            >
                              {[
                                {
                                  party: PARTY_NAME_REPUBLICAN,
                                  label: t('elections.republican'),
                                  color: primitiveColors.red4,
                                },
                                {
                                  party: PARTY_NAME_DEMOCRATIC,
                                  label: t('elections.democratic'),
                                  color: primitiveColors.blue7,
                                },
                              ].map(({ party, label, color }) => {
                                const selected = partyForThisElection === party;
                                return (
                                  <ButtonBase
                                    key={party}
                                    role="radio"
                                    aria-checked={selected}
                                    aria-label={label}
                                    onClick={() => {
                                      // Acts as a toggle (mirrors mobile PartyToggle): a click
                                      // anywhere switches to the other party rather than only
                                      // selecting the clicked half.
                                      const nextParty =
                                        partyForThisElection === PARTY_NAME_REPUBLICAN
                                          ? PARTY_NAME_DEMOCRATIC
                                          : PARTY_NAME_REPUBLICAN;
                                      selectParty(electionStateKey, nextParty);
                                      analytics.track('Sample Ballot Party Selected', {
                                        party:
                                          nextParty === PARTY_NAME_REPUBLICAN
                                            ? 'Republican'
                                            : 'Democratic',
                                        electionDate: election.election_date,
                                      });
                                    }}
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      lineHeight: 1,
                                      // Concentric inner radius (outer − 1px border): the
                                      // container clips to its padding box, so the fill must
                                      // match this radius or the corners read as mismatched.
                                      borderRadius: `calc(${BORDER_RADIUS} - 1px)`,
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: selected
                                        ? primitiveColors.white
                                        : semanticColors.textPrimary,
                                      backgroundColor: selected ? color : 'transparent',
                                      filter: selected ? 'saturate(70%)' : 'none',
                                      transition: 'background-color 0.2s ease, color 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: selected
                                          ? color
                                          : semanticColors.overlaySubtle,
                                      },
                                      '&:focus-visible': {
                                        outline: `2px solid ${primitiveColors.logoPurple}`,
                                        outlineOffset: '-2px',
                                      },
                                    }}
                                  >
                                    {label}
                                  </ButtonBase>
                                );
                              })}
                            </Box>
                          )}
                          {/* Contests for this election */}
                          {contestsByLevel.length > 0
                            ? contestsByLevel.map((group) => (
                                <Accordion
                                  key={`${group.level}-${partyForThisElection}`}
                                  defaultExpanded
                                  disableGutters
                                  elevation={0}
                                  slotProps={{
                                    transition: { unmountOnExit: true },
                                  }}
                                  sx={{
                                    '&::before': { display: 'none' },
                                    borderRadius: BORDER_RADIUS,
                                    '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                                  }}
                                >
                                  <AccordionSummary
                                    aria-label={t('elections.levelGroupAriaLabel', {
                                      displayName: group.displayName,
                                      count: group.contests.length,
                                    })}
                                    expandIcon={
                                      <ExpandMoreIcon
                                        sx={{ color: semanticColors.textOnPrimary }}
                                      />
                                    }
                                    sx={{
                                      backgroundColor: levelHeaderBg,
                                      filter: levelHeaderFilter,
                                      color: semanticColors.textOnPrimary,
                                      borderRadius: BORDER_RADIUS,
                                      '&.Mui-expanded': {
                                        borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                                      },
                                      p: 1,
                                      minHeight: 'unset',
                                      position: 'sticky',
                                      top: 0,
                                      zIndex: 2,
                                      '& .MuiAccordionSummary-content': { my: 0, pr: 3 },
                                      '& .MuiAccordionSummary-expandIconWrapper': (t) => ({
                                        position: 'absolute',
                                        right: t.spacing(1),
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        '&.Mui-expanded': {
                                          transform: 'translateY(-50%) rotate(180deg)',
                                        },
                                      }),
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                      <Typography
                                        variant="h6"
                                        component="h3"
                                        fontWeight="bold"
                                        lineHeight={1.1}
                                        sx={{ fontSize: { lg: 16, xs: 14 } }}
                                      >
                                        {group.displayName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          opacity: 0.85,
                                          lineHeight: 1.1,
                                          fontSize: { lg: 12, xs: 11 },
                                        }}
                                      >
                                        {t('elections.contestCount', {
                                          count: group.contests.length,
                                        })}
                                      </Typography>
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{
                                      px: 1,
                                      pt: 0.5,
                                      pb: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5,
                                      backgroundColor: primitiveColors.blue1,
                                      borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                                    }}
                                  >
                                    {group.contests.map((contest: Contest) => {
                                      const candidates = contest.candidates ?? [];
                                      const candidateCount = candidates.length;
                                      const isMergedTicket = candidates.some(
                                        (c) => c.runningMateFullName
                                      );
                                      const seats = contest.seatsUpForElection || 1;
                                      const electLabel = t(
                                        isMergedTicket
                                          ? 'elections.electLabelPair'
                                          : 'elections.electLabel',
                                        { count: seats }
                                      );

                                      // For merged tickets, a pair is "liked" if either the
                                      // president or running mate has a heart, so a partially
                                      // selected ticket isn't dropped.
                                      const isCandidateOrRunningMateLiked = (c: Candidate) =>
                                        isLiked(candidateKey(c)) ||
                                        (c.runningMate
                                          ? isLiked(candidateKey(c.runningMate))
                                          : false);
                                      const filterValue = contestFilters[contest.id] ?? 'all';
                                      const hasHearts = candidates.some(
                                        isCandidateOrRunningMateLiked
                                      );

                                      // Filter only when there are hearts. For picks-with-no-
                                      // hearts, keep the full list rendered (the accordion is
                                      // force-collapsed below) so MUI Collapse animates from the
                                      // full height instead of 0 to 0.
                                      const filteredCandidates =
                                        filterValue === 'picks' && hasHearts
                                          ? candidates.filter(isCandidateOrRunningMateLiked)
                                          : candidates;

                                      // Invariant: an expanded accordion is never empty — force
                                      // collapse when filter='picks' and the contest has no hearts.
                                      const wouldShowEmpty = filterValue === 'picks' && !hasHearts;
                                      const isExpanded =
                                        !wouldShowEmpty && (expandedContests[contest.id] ?? true);

                                      const handleFilterToggle = () => {
                                        const newFilter: YourPicksFilterValue =
                                          filterValue === 'picks' ? 'all' : 'picks';
                                        setContestFilters((prev) => ({
                                          ...prev,
                                          [contest.id]: newFilter,
                                        }));
                                        // Force-expand on toggle so filtering from a collapsed
                                        // accordion both flips and opens; wouldShowEmpty still
                                        // wins for picks-with-no-hearts (routed via bubbleClick).
                                        setExpandedContests((prev) => ({
                                          ...prev,
                                          [contest.id]: true,
                                        }));
                                        analytics.track('Sample Ballot Filter Toggled', {
                                          contestId: contest.id,
                                          filter: newFilter,
                                          hasHearts,
                                        });
                                      };

                                      // Compare chip: send the contest's candidates to chat,
                                      // like the measure "Explain" button sends its prompt.
                                      const handleCompare = () => {
                                        if (isProcessing || pendingAction) return;
                                        setPendingAction({ kind: 'compare', id: contest.id });
                                        analytics.track('Sample Ballot Compare Clicked', {
                                          contestId: contest.id,
                                          candidateCount,
                                        });
                                        sendChat(
                                          t('elections.compareChatPrompt', {
                                            office: contest.name,
                                            candidates: candidates
                                              .map((c) => c.fullName)
                                              .join(', '),
                                          }),
                                          { ballotRef: { kind: 'contest', contest_id: contest.id } }
                                        );
                                        handleCloseBallot();
                                      };

                                      const compareActionState = getBallotActionState({
                                        kind: 'compare',
                                        id: contest.id,
                                        pendingAction,
                                        isProcessing,
                                      });

                                      // Used by the collapsed-summary-row to re-expand. Reset the
                                      // filter to 'all' only when 'picks' would be empty, else
                                      // preserve the user's filter intent.
                                      const expandContest = () => {
                                        setExpandedContests((prev) => ({
                                          ...prev,
                                          [contest.id]: true,
                                        }));
                                        if (wouldShowEmpty) {
                                          setContestFilters((prev) => ({
                                            ...prev,
                                            [contest.id]: 'all',
                                          }));
                                        }
                                      };

                                      return (
                                        <Box
                                          key={contest.id}
                                          sx={{
                                            borderRadius: BORDER_RADIUS,
                                            overflow: 'hidden',
                                          }}
                                        >
                                          {candidateCount <= 1 && !isMergedTicket ? (
                                            <>
                                              <Box
                                                sx={{
                                                  backgroundColor: primitiveColors.gray28,
                                                  color: semanticColors.textOnPrimary,
                                                  p: 1,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                }}
                                              >
                                                <Typography
                                                  variant="h6"
                                                  component="h4"
                                                  fontWeight="bold"
                                                  lineHeight="1.1"
                                                  sx={{ fontSize: { lg: 16, xs: 14 } }}
                                                >
                                                  {contest.name}
                                                  <Typography
                                                    component="span"
                                                    sx={{
                                                      fontSize: { lg: 13, xs: 12 },
                                                      fontWeight: 400,
                                                      opacity: 0.85,
                                                      ml: 0.75,
                                                    }}
                                                  >
                                                    {electLabel}
                                                  </Typography>
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  px: 1,
                                                  backgroundColor: semanticColors.surface,
                                                }}
                                              >
                                                {candidates.map(
                                                  (
                                                    candidate: Candidate,
                                                    candidateIndex: number
                                                  ) => (
                                                    <BallotCandidate
                                                      key={candidateKey(candidate)}
                                                      candidate={candidate}
                                                      contestId={contest.id}
                                                      isLast={
                                                        candidateIndex === candidates.length - 1
                                                      }
                                                      isMobile={isMobile}
                                                      onCloseBallot={handleCloseBallot}
                                                      heartsOverride={viewedHearts}
                                                      heartsReadOnly={viewedHeartsReadOnly}
                                                    />
                                                  )
                                                )}
                                              </Box>
                                            </>
                                          ) : (
                                            <>
                                              <Accordion
                                                expanded={isExpanded}
                                                onChange={(_, expanded) => {
                                                  setExpandedContests((prev) => ({
                                                    ...prev,
                                                    [contest.id]: expanded,
                                                  }));
                                                  if (expanded) {
                                                    // Reset filter to 'all' only when 'picks'
                                                    // would be empty, else preserve the user's
                                                    // filter intent for re-expand.
                                                    if (wouldShowEmpty) {
                                                      setContestFilters((prev) => ({
                                                        ...prev,
                                                        [contest.id]: 'all',
                                                      }));
                                                    }
                                                    trackBallotItemViewed();
                                                  }
                                                }}
                                                disableGutters
                                                elevation={0}
                                                slotProps={{
                                                  transition: { unmountOnExit: true },
                                                }}
                                                sx={{
                                                  borderRadius: 0,
                                                  overflow: 'hidden',
                                                  '&::before': { display: 'none' },
                                                }}
                                              >
                                                <AccordionSummary
                                                  aria-label={`${contest.name} ${electLabel}`}
                                                  expandIcon={
                                                    <ExpandMoreIcon
                                                      sx={{
                                                        color: semanticColors.textOnPrimary,
                                                      }}
                                                    />
                                                  }
                                                  sx={{
                                                    backgroundColor: primitiveColors.gray28,
                                                    color: semanticColors.textOnPrimary,
                                                    p: 1,
                                                    minHeight: 'unset',
                                                    position: 'relative',
                                                    '& .MuiAccordionSummary-content': {
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between',
                                                      my: 0,
                                                      pr: 3,
                                                    },
                                                    '& .MuiAccordionSummary-expandIconWrapper': (
                                                      t
                                                    ) => ({
                                                      position: 'absolute',
                                                      right: t.spacing(1),
                                                      top: '50%',
                                                      transform: 'translateY(-50%)',
                                                      '&.Mui-expanded': {
                                                        transform:
                                                          'translateY(-50%) rotate(180deg)',
                                                      },
                                                    }),
                                                  }}
                                                >
                                                  <Box>
                                                    <Typography
                                                      variant="h6"
                                                      component="h4"
                                                      fontWeight="bold"
                                                      lineHeight="1.1"
                                                      sx={{ fontSize: { lg: 16, xs: 14 } }}
                                                    >
                                                      {contest.name}
                                                      <Typography
                                                        component="span"
                                                        sx={{
                                                          fontSize: { lg: 13, xs: 12 },
                                                          fontWeight: 400,
                                                          opacity: 0.85,
                                                          ml: 0.75,
                                                        }}
                                                      >
                                                        {electLabel}
                                                      </Typography>
                                                    </Typography>
                                                  </Box>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.75,
                                                    }}
                                                  >
                                                    {candidateCount > 1 && (
                                                      // Same gradient chip as the measure
                                                      // "what does YES/NO mean" button.
                                                      <Chip
                                                        label={
                                                          compareActionState.isThisActionPending
                                                            ? t('elections.comparing')
                                                            : t('elections.compare')
                                                        }
                                                        size="small"
                                                        disabled={compareActionState.isDisabled}
                                                        aria-disabled={
                                                          compareActionState.isDisabled
                                                        }
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label={t(
                                                          'elections.compareAriaLabel',
                                                          {
                                                            office: contest.name,
                                                          }
                                                        )}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (isProcessing || pendingAction) return;
                                                          handleCompare();
                                                        }}
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (isProcessing || pendingAction)
                                                              return;
                                                            handleCompare();
                                                          }
                                                        }}
                                                        sx={{
                                                          fontFamily: "'DM Sans', sans-serif",
                                                          fontSize: 11,
                                                          // Weight matches the Full Profile chip
                                                          // (unset → Chip default), not the heavier
                                                          // measure button.
                                                          letterSpacing: '0.19px',
                                                          color: semanticColors.textPrimary,
                                                          // 5px: 8px pills at this height.
                                                          borderRadius: '5px',
                                                          border: '2px solid transparent',
                                                          // Height matches the picks toggle pill
                                                          // sitting beside it in the header.
                                                          height: { lg: '18px', xs: '16px' },
                                                          background: gradientBorderBackground(
                                                            semanticColors.background,
                                                            semanticGradients.primary
                                                          ),
                                                          '& .MuiChip-label': {
                                                            px: 1.5,
                                                            py: 0,
                                                            lineHeight: 1,
                                                          },
                                                          // Gradient background suppresses the
                                                          // focus ring, so paint an outline.
                                                          '&.Mui-focusVisible': {
                                                            outline: `2px solid ${primitiveColors.logoPurple}`,
                                                            outlineOffset: '2px',
                                                            // White gap for focus-ring contrast.
                                                            boxShadow: `0 0 0 2px ${primitiveColors.white}`,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                    <YourPicksFilter
                                                      value={filterValue}
                                                      onToggle={handleFilterToggle}
                                                      bubbleClick={!isExpanded && !hasHearts}
                                                      hasHearts={hasHearts}
                                                    />
                                                  </Box>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ px: 1, py: 0 }}>
                                                  {isMergedTicket
                                                    ? filteredCandidates.map(
                                                        (
                                                          candidate: Candidate,
                                                          pairIndex: number
                                                        ) => (
                                                          <Box
                                                            key={`pair-${candidate.id}`}
                                                            sx={{
                                                              backgroundColor:
                                                                pairIndex % 2 === 1
                                                                  ? semanticColors.surfaceWarm
                                                                  : semanticColors.surface,
                                                              borderBottom:
                                                                pairIndex <
                                                                filteredCandidates.length - 1
                                                                  ? `1px solid ${primitiveColors.black}`
                                                                  : 'none',
                                                            }}
                                                          >
                                                            <BallotCandidate
                                                              key={candidateKey(candidate)}
                                                              candidate={candidate}
                                                              contestId={contest.id}
                                                              isLast
                                                              isMobile={isMobile}
                                                              onCloseBallot={handleCloseBallot}
                                                              heartsOverride={viewedHearts}
                                                              heartsReadOnly={viewedHeartsReadOnly}
                                                              transparentBackground
                                                            />
                                                            {candidate.runningMate && (
                                                              <BallotCandidate
                                                                key={candidateKey(
                                                                  candidate.runningMate
                                                                )}
                                                                contestId={contest.id}
                                                                candidate={{
                                                                  ...candidate.runningMate,
                                                                  isIncumbent: false,
                                                                  contact: { campaign: {} },
                                                                }}
                                                                isLast
                                                                isMobile={isMobile}
                                                                onCloseBallot={handleCloseBallot}
                                                                heartsOverride={viewedHearts}
                                                                heartsReadOnly={
                                                                  viewedHeartsReadOnly
                                                                }
                                                                transparentBackground
                                                              />
                                                            )}
                                                          </Box>
                                                        )
                                                      )
                                                    : filteredCandidates.map(
                                                        (
                                                          candidate: Candidate,
                                                          candidateIndex: number
                                                        ) => (
                                                          <BallotCandidate
                                                            key={candidateKey(candidate)}
                                                            candidate={candidate}
                                                            contestId={contest.id}
                                                            isLast={
                                                              candidateIndex ===
                                                              filteredCandidates.length - 1
                                                            }
                                                            isMobile={isMobile}
                                                            onCloseBallot={handleCloseBallot}
                                                            heartsOverride={viewedHearts}
                                                            heartsReadOnly={viewedHeartsReadOnly}
                                                          />
                                                        )
                                                      )}
                                                </AccordionDetails>
                                              </Accordion>

                                              {/* Summary row: shown when collapsed */}
                                              <Collapse
                                                in={!isExpanded}
                                                timeout="auto"
                                                unmountOnExit
                                              >
                                                <ButtonBase
                                                  disableRipple
                                                  aria-label={
                                                    isMergedTicket
                                                      ? t('elections.showAllPairsAriaLabel', {
                                                          count: candidateCount,
                                                          name: contest.name,
                                                        })
                                                      : t('elections.showAllCandidatesAriaLabel', {
                                                          count: candidateCount,
                                                          name: contest.name,
                                                        })
                                                  }
                                                  onClick={expandContest}
                                                  sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    px: 3,
                                                    py: 1,
                                                    backgroundColor: semanticColors.surface,
                                                    '&:hover': {
                                                      backgroundColor: semanticColors.surfaceWarm,
                                                    },
                                                  }}
                                                >
                                                  <GroupIcon
                                                    sx={{
                                                      color: semanticColors.textMediumEmphasis,
                                                      fontSize: { lg: 16, xs: 14 },
                                                    }}
                                                  />
                                                  <Typography
                                                    component="span"
                                                    sx={{
                                                      fontFamily: "'DM Sans', sans-serif",
                                                      fontSize: { lg: 16, xs: 14 },
                                                      fontWeight: 700,
                                                      lineHeight: 1.1,
                                                      color: semanticColors.textMediumEmphasis,
                                                    }}
                                                  >
                                                    {isMergedTicket
                                                      ? t('elections.pairCount', {
                                                          count: candidateCount,
                                                        })
                                                      : t('elections.candidateCount', {
                                                          count: candidateCount,
                                                        })}
                                                  </Typography>
                                                </ButtonBase>
                                              </Collapse>
                                            </>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </AccordionDetails>
                                </Accordion>
                              ))
                            : !hasMeasures && (
                                <Paper
                                  elevation={1}
                                  sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    backgroundColor: semanticColors.surface,
                                    borderRadius: BORDER_RADIUS,
                                  }}
                                >
                                  <Typography variant="body1" color="text.secondary">
                                    {t('elections.noContests')}
                                  </Typography>
                                </Paper>
                              )}

                          {/* Ballot Measures for this election */}
                          {hasMeasures && (
                            <Accordion
                              key={`measures-${partyForThisElection}`}
                              defaultExpanded
                              disableGutters
                              elevation={0}
                              sx={{
                                '&::before': { display: 'none' },
                                borderRadius: BORDER_RADIUS,
                                '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                              }}
                            >
                              <AccordionSummary
                                aria-label={t('elections.measureGroupAriaLabel', {
                                  count: election.ballot_measures.length,
                                })}
                                expandIcon={
                                  <ExpandMoreIcon sx={{ color: semanticColors.textOnPrimary }} />
                                }
                                sx={{
                                  backgroundColor: levelHeaderBg,
                                  filter: levelHeaderFilter,
                                  color: semanticColors.textOnPrimary,
                                  borderRadius: BORDER_RADIUS,
                                  '&.Mui-expanded': {
                                    borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                                  },
                                  p: 1,
                                  minHeight: 'unset',
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 2,
                                  '& .MuiAccordionSummary-content': { my: 0, pr: 3 },
                                  '& .MuiAccordionSummary-expandIconWrapper': (t) => ({
                                    position: 'absolute',
                                    right: t.spacing(1),
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    '&.Mui-expanded': {
                                      transform: 'translateY(-50%) rotate(180deg)',
                                    },
                                  }),
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography
                                    variant="h6"
                                    component="h3"
                                    fontWeight="bold"
                                    lineHeight={1.1}
                                    sx={{ fontSize: { lg: 16, xs: 14 } }}
                                  >
                                    {t('elections.ballotMeasures')}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      opacity: 0.85,
                                      lineHeight: 1.1,
                                      fontSize: { lg: 12, xs: 11 },
                                    }}
                                  >
                                    {t('elections.measureCount', {
                                      count: election.ballot_measures.length,
                                    })}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                sx={{
                                  px: 1,
                                  pt: 0.5,
                                  pb: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  backgroundColor: primitiveColors.blue1,
                                  borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                                }}
                              >
                                {election.ballot_measures.map(
                                  (measure: BallotMeasure, measureIndex: number) => {
                                    // One resolved name for the heading and the vote
                                    // control's labels, matching the mobile app's
                                    // chain. The last resort names VoteMate as the
                                    // source of the number, because the position is
                                    // VoteMate's own ordering — no feed carries the
                                    // state's measure number as a field, and the
                                    // printed ballot's order is not in our data.
                                    const measureDisplayName =
                                      measure.streamlinedName ||
                                      measure.shortName ||
                                      measure.name ||
                                      t('elections.measureFallbackName', {
                                        index: measureIndex + 1,
                                      });
                                    // Send the measure's prompt to chat, close the sheet.
                                    const handleExplain = () => {
                                      if (isProcessing || pendingAction) return;
                                      setPendingAction({ kind: 'explain', id: measure.id });
                                      sendChat(
                                        t('elections.explainChatPrompt', {
                                          name: measureDisplayName,
                                        }),
                                        { ballotRef: { kind: 'measure', measure_id: measure.id } }
                                      );
                                      handleCloseBallot();
                                    };
                                    const explainActionState = getBallotActionState({
                                      kind: 'explain',
                                      id: measure.id,
                                      pendingAction,
                                      isProcessing,
                                    });
                                    return (
                                      <Box
                                        key={measure.id}
                                        sx={{ borderRadius: BORDER_RADIUS, overflow: 'hidden' }}
                                      >
                                        <Accordion
                                          defaultExpanded
                                          disableGutters
                                          elevation={0}
                                          sx={{
                                            borderRadius: 0,
                                            '&::before': { display: 'none' },
                                          }}
                                        >
                                          <AccordionSummary
                                            expandIcon={
                                              <ExpandMoreIcon
                                                sx={{ color: semanticColors.textOnPrimary }}
                                              />
                                            }
                                            sx={{
                                              backgroundColor: primitiveColors.gray28,
                                              color: semanticColors.textOnPrimary,
                                              p: 1,
                                              minHeight: 'unset',
                                              position: 'relative',
                                              // Row so the Explain chip sits beside the title;
                                              // pr:5 clears the absolute expand icon.
                                              '& .MuiAccordionSummary-content': {
                                                my: 0,
                                                pr: 5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                              },
                                              '& .MuiAccordionSummary-expandIconWrapper': (t) => ({
                                                position: 'absolute',
                                                right: t.spacing(1),
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                '&.Mui-expanded': {
                                                  transform: 'translateY(-50%) rotate(180deg)',
                                                },
                                              }),
                                            }}
                                          >
                                            <Typography
                                              variant="h6"
                                              component="h4"
                                              fontWeight="bold"
                                              lineHeight="1.1"
                                              sx={{
                                                fontSize: { lg: 16, xs: 14 },
                                                flexGrow: 1,
                                                minWidth: 0,
                                              }}
                                            >
                                              {measureDisplayName}
                                            </Typography>
                                            <Chip
                                              label={
                                                explainActionState.isThisActionPending
                                                  ? t('elections.explaining')
                                                  : t('elections.explain')
                                              }
                                              size="small"
                                              disabled={explainActionState.isDisabled}
                                              aria-disabled={explainActionState.isDisabled}
                                              role="button"
                                              tabIndex={0}
                                              aria-label={t('elections.explainAriaLabel', {
                                                name: measureDisplayName,
                                              })}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (isProcessing || pendingAction) return;
                                                handleExplain();
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  if (isProcessing || pendingAction) return;
                                                  handleExplain();
                                                }
                                              }}
                                              sx={{
                                                flexShrink: 0,
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: 11,
                                                // Weight matches the Full Profile chip
                                                // (unset → Chip default), like Compare.
                                                letterSpacing: '0.19px',
                                                color: semanticColors.textPrimary,
                                                // 5px: 8px pills at this height (see Compare).
                                                borderRadius: '5px',
                                                border: '2px solid transparent',
                                                // Height matches the contest Compare chip.
                                                height: { lg: '18px', xs: '16px' },
                                                background: gradientBorderBackground(
                                                  semanticColors.background,
                                                  semanticGradients.primary
                                                ),
                                                '& .MuiChip-label': {
                                                  px: 1.5,
                                                  py: 0,
                                                  lineHeight: 1,
                                                },
                                                // Gradient background suppresses the
                                                // default focus ring, so paint an outline.
                                                '&.Mui-focusVisible': {
                                                  outline: `2px solid ${primitiveColors.logoPurple}`,
                                                  outlineOffset: '2px',
                                                  // White gap for focus-ring contrast.
                                                  boxShadow: `0 0 0 2px ${primitiveColors.white}`,
                                                },
                                              }}
                                            />
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ px: 1, pt: 2, pb: 1 }}>
                                            <Typography
                                              variant="body1"
                                              color="text.primary"
                                              // Match the measure title's responsive size so the
                                              // summary reads like mobile, not larger at xs.
                                              sx={{ fontSize: { lg: 16, xs: 14 }, mb: 2 }}
                                            >
                                              {measure.summary}
                                            </Typography>
                                            <Box
                                              // The two choices are one decision, so
                                              // they're exposed as a labelled group.
                                              // They stay `aria-pressed` toggles rather
                                              // than radios because picking the chosen
                                              // one again clears the position back to
                                              // undecided, which radio semantics have no
                                              // way to express.
                                              role="group"
                                              aria-label={t('elections.voteGroupAriaLabel', {
                                                name: measureDisplayName,
                                              })}
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                mb: 2,
                                              }}
                                            >
                                              {(['yes', 'no'] as const).map((choice) => {
                                                const selected =
                                                  effectiveGetMeasureVote(measure.id) === choice;
                                                // Read-only (past-election) labels track
                                                // `selected` too, so a saved Yes announces the
                                                // No choice as unselected rather than as a
                                                // second vote.
                                                const ariaLabelKey = viewedHeartsReadOnly
                                                  ? selected
                                                    ? MEASURE_VOTE_ARIA_KEYS.readOnlySelected[
                                                        choice
                                                      ]
                                                    : MEASURE_VOTE_ARIA_KEYS.readOnlyUnselected[
                                                        choice
                                                      ]
                                                  : selected
                                                    ? MEASURE_VOTE_ARIA_KEYS.remove[choice]
                                                    : MEASURE_VOTE_ARIA_KEYS.cast[choice];
                                                // Ballotpedia's YES/NO text, kept beside DW's.
                                                const bpVote = parseBpVote(
                                                  choice === 'yes'
                                                    ? measure.bp?.yes_vote
                                                    : measure.bp?.no_vote
                                                );
                                                return (
                                                  <Box
                                                    key={choice}
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'flex-start',
                                                      gap: 1,
                                                    }}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      disabled={viewedHeartsReadOnly}
                                                      onClick={() =>
                                                        handleToggleMeasureVote(measure.id, choice)
                                                      }
                                                      aria-label={t(ariaLabelKey, {
                                                        name: measureDisplayName,
                                                      })}
                                                      aria-pressed={selected}
                                                      sx={{
                                                        p: 0,
                                                        // Row is top-aligned for multi-line BP
                                                        // text; nudge heart onto the first line.
                                                        mt: '2px',
                                                        color: selected
                                                          ? primitiveColors.red4
                                                          : semanticColors.textMediumEmphasis,
                                                        // Preserve the semantic color when
                                                        // read-only (past-election records)
                                                        // instead of MUI's default disabled
                                                        // gray, so a viewer can still see which
                                                        // way they voted.
                                                        '&.Mui-disabled': {
                                                          color: selected
                                                            ? primitiveColors.red4
                                                            : semanticColors.textMediumEmphasis,
                                                        },
                                                      }}
                                                    >
                                                      {selected ? (
                                                        <FavoriteIcon sx={{ fontSize: 16 }} />
                                                      ) : (
                                                        <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                                                      )}
                                                    </IconButton>
                                                    <Typography
                                                      variant="body2"
                                                      component="div"
                                                      sx={{
                                                        '& p': { display: 'inline', m: 0 },
                                                        '& ul, & ol': { m: 0, mt: 0.5, pl: 3 },
                                                        '& li': { mb: 0.25 },
                                                      }}
                                                    >
                                                      <Box component="span" fontWeight="bold">
                                                        {choice === 'yes'
                                                          ? t('elections.yesLabel')
                                                          : t('elections.noLabel')}
                                                      </Box>
                                                      {bpVote ? (
                                                        <>
                                                          {bpVote.intro
                                                            ? ` — ${bpVote.intro}`
                                                            : null}
                                                          {bpVote.items.length > 0 ? (
                                                            <Box
                                                              component="ul"
                                                              sx={{ m: 0, mt: 0.5, pl: 3 }}
                                                            >
                                                              {bpVote.items.map((item, i) => (
                                                                <li key={i}>{item}</li>
                                                              ))}
                                                            </Box>
                                                          ) : null}
                                                        </>
                                                      ) : null}
                                                    </Typography>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                            <Accordion
                                              disableGutters
                                              expanded={
                                                isMeasuresInitialized &&
                                                (expandedMeasures[measure.id] ?? false)
                                              }
                                              onChange={(_, expanded) => {
                                                setExpandedMeasures((prev) => ({
                                                  ...prev,
                                                  [measure.id]: expanded,
                                                }));
                                                if (expanded) trackBallotItemViewed();
                                              }}
                                              sx={{
                                                boxShadow: 'none',
                                                '&:before': { display: 'none' },
                                              }}
                                              slots={{ heading: Typography }}
                                              slotProps={{ heading: { component: 'span' } }}
                                            >
                                              <AccordionSummary
                                                expandIcon={null}
                                                aria-label={t('elections.fullTextAriaLabel', {
                                                  index: measureIndex + 1,
                                                  total: election.ballot_measures.length,
                                                })}
                                                sx={{
                                                  px: 0,
                                                  minHeight: 'auto',
                                                  '& .MuiAccordionSummary-content': { margin: 0 },
                                                  '&.Mui-expanded .expand-icon': {
                                                    transform: 'rotate(180deg)',
                                                  },
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    component="span"
                                                    fontWeight="bold"
                                                  >
                                                    {t('elections.fullText')}
                                                  </Typography>
                                                  <ExpandMoreIcon
                                                    aria-hidden="true"
                                                    className="expand-icon"
                                                    sx={{
                                                      fontSize: '1rem',
                                                      transition: 'transform 0.2s ease-in-out',
                                                    }}
                                                  />
                                                </Box>
                                              </AccordionSummary>
                                              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ display: 'block' }}
                                                >
                                                  {measure.ballotQuestion ? (
                                                    decodeHtml(measure.ballotQuestion)
                                                  ) : (
                                                    // No full text from DW or BP — link to the
                                                    // election office, highlighting it in the
                                                    // Voting Info tab (like the local-races link).
                                                    <Link
                                                      component="button"
                                                      type="button"
                                                      onClick={() => {
                                                        setVotingInfoTabActive(true);
                                                        setPendingElectionOfficeHighlight(
                                                          (c) => c + 1
                                                        );
                                                      }}
                                                      sx={{
                                                        color: semanticColors.textPrimary,
                                                        fontWeight: 600,
                                                        textAlign: 'left',
                                                        verticalAlign: 'baseline',
                                                        fontSize: 'inherit',
                                                        lineHeight: 'inherit',
                                                        '&:focus-visible': {
                                                          outline: `2px solid ${primitiveColors.logoPurple}`,
                                                          outlineOffset: '2px',
                                                        },
                                                      }}
                                                    >
                                                      {t('elections.fullTextFallback')}
                                                    </Link>
                                                  )}
                                                </Typography>
                                              </AccordionDetails>
                                            </Accordion>
                                          </AccordionDetails>
                                        </Accordion>
                                      </Box>
                                    );
                                  }
                                )}
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </AccordionDetails>
                      </Accordion>

                      {/* Summary row: shown when election card is collapsed */}
                      <Collapse in={!isElectionExpanded} timeout="auto" unmountOnExit>
                        <ButtonBase
                          disableRipple
                          aria-label={t('elections.showAllContestsAriaLabel', {
                            count:
                              filteredContests.length + (election.ballot_measures?.length ?? 0),
                            name: electionName,
                          })}
                          onClick={() =>
                            setExpandedElections((prev) => ({
                              ...prev,
                              [electionStateKey]: true,
                            }))
                          }
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 1,
                            backgroundColor: semanticColors.surface,
                            borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                            '&:hover': {
                              backgroundColor: semanticColors.surfaceWarm,
                            },
                          }}
                        >
                          <Box
                            component="svg"
                            aria-hidden="true"
                            width="1em"
                            height="1em"
                            viewBox="0 -1 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            sx={{
                              color: semanticColors.textMediumEmphasis,
                              fontSize: { lg: 16, xs: 14 },
                            }}
                          >
                            <path
                              d="M2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V16C20 16.55 19.8042 17.0208 19.4125 17.4125C19.0208 17.8042 18.55 18 18 18H2ZM2 16H18V2H2V16ZM3 14H8V12H3V14ZM12.55 12L17.5 7.05L16.075 5.625L12.55 9.175L11.125 7.75L9.725 9.175L12.55 12ZM3 10H8V8H3V10ZM3 6H8V4H3V6Z"
                              fill="currentColor"
                            />
                          </Box>
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: { lg: 16, xs: 14 },
                              fontWeight: 700,
                              lineHeight: 1.1,
                              color: semanticColors.textMediumEmphasis,
                            }}
                          >
                            {isNational
                              ? isDC
                                ? t('elections.districtWideContest', {
                                    count:
                                      filteredContests.length +
                                      (election.ballot_measures?.length ?? 0),
                                  })
                                : t('elections.stateWideContest', {
                                    count:
                                      filteredContests.length +
                                      (election.ballot_measures?.length ?? 0),
                                  })
                              : t('elections.contestSummary', {
                                  count:
                                    filteredContests.length +
                                    (election.ballot_measures?.length ?? 0),
                                })}
                          </Typography>
                        </ButtonBase>
                      </Collapse>
                    </Box>
                  );
                };

                // National General: bucket the flat (state, election) cards into three
                // alphabetical groups, collapsed by default (trims 50+ visible cards to 3).
                // Primary & Special and personal-mode views are unaffected.
                if (isNational && activeCategory === 'general') {
                  // Pre-compute each group's present states + dynamic label. Sort by full
                  // state name (not code) so "District of Columbia" lands between Delaware and
                  // Florida. Empty groups are skipped — a "(0)" header adds nothing.
                  const groupViews = ALPHA_GROUPS.map((group) => {
                    const groupElections = electionsToRender.filter((e) => {
                      const sc = (e as Election & { state_code?: string }).state_code;
                      return sc != null && group.states.has(sc);
                    });
                    const presentStateCodes = Array.from(
                      new Set(
                        groupElections
                          .map((e) => (e as Election & { state_code?: string }).state_code)
                          .filter((sc): sc is string => sc != null)
                      )
                    );
                    const sortedByName = presentStateCodes.sort((a, b) => {
                      const an = STATE_NAMES[a] ?? a;
                      const bn = STATE_NAMES[b] ?? b;
                      return an.localeCompare(bn);
                    });
                    const firstName = sortedByName.length
                      ? (STATE_NAMES[sortedByName[0]] ?? sortedByName[0])
                      : null;
                    const lastName = sortedByName.length
                      ? (STATE_NAMES[sortedByName[sortedByName.length - 1]] ??
                        sortedByName[sortedByName.length - 1])
                      : null;
                    const dynamicLabel =
                      firstName && lastName
                        ? firstName === lastName
                          ? firstName
                          : `${firstName} – ${lastName}`
                        : group.label; // fallback (unused since empty groups are skipped below)
                    return { group, groupElections, dynamicLabel };
                  }).filter((view) => view.groupElections.length > 0);

                  return (
                    <>
                      {groupViews.map(({ group, groupElections, dynamicLabel }) => {
                        const isOpen = expandedAlphaGroups[group.id] ?? false;
                        return (
                          <Accordion
                            key={group.id}
                            expanded={isOpen}
                            onChange={() =>
                              setExpandedAlphaGroups((prev) => ({
                                ...prev,
                                [group.id]: !isOpen,
                              }))
                            }
                            disableGutters
                            elevation={0}
                            slotProps={{ transition: { unmountOnExit: true } }}
                            sx={{
                              backgroundColor: semanticColors.themeAqua,
                              borderRadius: BORDER_RADIUS,
                              '&::before': { display: 'none' },
                              '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMoreIcon sx={{ color: semanticColors.textPrimary }} />
                              }
                              sx={votingInfoSectionHeaderSx}
                            >
                              <Typography
                                variant="h6"
                                component="h2"
                                fontWeight="bold"
                                lineHeight={1.1}
                                sx={{ fontSize: { xs: 14, lg: 16 } }}
                              >
                                {dynamicLabel} ({groupElections.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                p: 1,
                                backgroundColor: semanticColors.surface,
                                borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {groupElections.map(renderElectionCard)}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </>
                  );
                }

                if (isNational && activeCategory === 'primary_special') {
                  // Time-bucket rendering for Primary & Special, parallel to the General tab's
                  // alphabetical grouping: three fixed aqua accordions with a dynamic first-last
                  // month label. Empty buckets are skipped; electionsToRender's date-then-name
                  // order is preserved within each bucket.
                  const extractMonth = (e: Election): number | null => {
                    const d = e.election_date;
                    if (!d || typeof d !== 'string' || d.length < 7) return null;
                    const m = parseInt(d.substring(5, 7), 10);
                    return Number.isFinite(m) && m >= 1 && m <= 12 ? m : null;
                  };
                  const extractYear = (e: Election): number | null => {
                    const d = e.election_date;
                    if (!d || typeof d !== 'string' || d.length < 4) return null;
                    const y = parseInt(d.substring(0, 4), 10);
                    return Number.isFinite(y) ? y : null;
                  };
                  const bucketViews = MONTH_BUCKETS.map((bucket) => {
                    const bucketElections = electionsToRender.filter((e) => {
                      const m = extractMonth(e);
                      return m != null && bucket.months.has(m);
                    });
                    const presentMonths = Array.from(
                      new Set(
                        bucketElections.map(extractMonth).filter((m): m is number => m != null)
                      )
                    ).sort((a, b) => a - b);
                    const years = Array.from(
                      new Set(
                        bucketElections.map(extractYear).filter((y): y is number => y != null)
                      )
                    );
                    const firstMonth = presentMonths[0];
                    const lastMonth = presentMonths[presentMonths.length - 1];
                    // Same first-vs-last collapse as the General tab. Append the year when the
                    // bucket shares one (the common case); drop it for rare multi-year buckets.
                    // Fallback below is unused — empty buckets are skipped.
                    let dynamicLabel = bucket.label;
                    if (firstMonth != null && lastMonth != null) {
                      const monthRange =
                        firstMonth === lastMonth
                          ? monthNamesLong[firstMonth]
                          : `${monthNamesLong[firstMonth]} – ${monthNamesLong[lastMonth]}`;
                      dynamicLabel = years.length === 1 ? `${monthRange} ${years[0]}` : monthRange;
                    }
                    return { bucket, bucketElections, dynamicLabel };
                  }).filter((view) => view.bucketElections.length > 0);

                  return (
                    <>
                      {bucketViews.map(({ bucket, bucketElections, dynamicLabel }) => {
                        const isOpen = expandedTimeBuckets[bucket.id] ?? false;
                        return (
                          <Accordion
                            key={bucket.id}
                            expanded={isOpen}
                            onChange={() =>
                              setExpandedTimeBuckets((prev) => ({
                                ...prev,
                                [bucket.id]: !isOpen,
                              }))
                            }
                            disableGutters
                            elevation={0}
                            slotProps={{ transition: { unmountOnExit: true } }}
                            sx={{
                              backgroundColor: semanticColors.themeAqua,
                              borderRadius: BORDER_RADIUS,
                              '&::before': { display: 'none' },
                              '&.MuiPaper-root': { borderRadius: BORDER_RADIUS },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMoreIcon sx={{ color: semanticColors.textPrimary }} />
                              }
                              sx={votingInfoSectionHeaderSx}
                            >
                              <Typography
                                variant="h6"
                                component="h2"
                                fontWeight="bold"
                                lineHeight={1.1}
                                sx={{ fontSize: { xs: 14, lg: 16 } }}
                              >
                                {dynamicLabel} ({bucketElections.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                p: 1,
                                backgroundColor: semanticColors.surface,
                                borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {bucketElections.map(renderElectionCard)}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </>
                  );
                }

                return <>{electionsToRender.map(renderElectionCard)}</>;
              })()
            ) : (
              <Paper
                elevation={1}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: semanticColors.surface,
                  borderRadius: BORDER_RADIUS,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {isElectionDatePast || !formattedDate
                    ? t('upcomingElection.preparing')
                    : t('upcomingElection.notYetAvailable', {
                        date: new Intl.DateTimeFormat(locale, {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          timeZone: 'UTC',
                        }).format(formattedDate),
                      })}
                </Typography>
              </Paper>
            )}
          </Box>

          <div ref={disclaimerRef} />

          {/* Inline survey + donate CTA */}
          <Box
            sx={{
              mx: 1,
              mb: 1,
              mt: 1,
              py: '20px',
              px: '24px',
              borderRadius: BORDER_RADIUS,
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: `1px solid ${semanticColors.borderLight}`,
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '15px',
                color: semanticColors.textBody,
                mb: '4px',
              }}
            >
              {t('cta.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: semanticColors.textSecondary,
                mb: '16px',
                lineHeight: 1.5,
              }}
            >
              {t('cta.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <ButtonBase
                onClick={() => openSurveyPanel()}
                aria-label={t('cta.surveyAriaLabel')}
                sx={{
                  flex: 1,
                  py: '10px',
                  borderRadius: BORDER_RADIUS,
                  backgroundColor: CHIP_COLOR,
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  minHeight: '40px',
                  '&:hover': { backgroundColor: '#4e5d6b' },
                  '&:focus-visible': { outline: `2px solid ${CHIP_COLOR}`, outlineOffset: '2px' },
                }}
              >
                {t('cta.survey')}
              </ButtonBase>
              <ButtonBase
                onClick={() => openExternalUrl(EXTERNAL_URLS.DONATE)}
                aria-label={t('cta.donateAriaLabel')}
                sx={{
                  flex: 1,
                  py: '10px',
                  borderRadius: BORDER_RADIUS,
                  border: '1.5px solid #D9374A',
                  color: '#D9374A',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  minHeight: '40px',
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,83,99,0.06)' },
                  '&:focus-visible': { outline: '2px solid #D9374A', outlineOffset: '2px' },
                }}
              >
                {t('cta.donate')}
              </ButtonBase>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
});

// Wrapped component with error boundary
const SampleBallotViewWithErrorBoundary: React.FC<SampleBallotViewProps> = (props) => {
  return (
    <SampleBallotErrorBoundary>
      <SampleBallotView {...props} />
    </SampleBallotErrorBoundary>
  );
};

export default SampleBallotViewWithErrorBoundary;
